// ============================================================
// KLUBBMESTERSKAP (KM) ENGINE — moved out of the monolith
// (Phase 3, incremental split).
//
// Pure, self-contained front-half of the club-championship engine: match-format
// evaluation (sets/games), group seeding (snake / rule-based / sequential),
// round-robin scheduling, cross-over + lucky-loser + FIP bracket builders,
// group standings, capacity/time-budget estimators, the main tournament factory
// (createKlubbmesterskapTournament) and the calendar scheduler
// (scheduleKMMatches + applyCourtPriority).
//
// No storage, no React, no ELO. The BACK-HALF KM result/ELO functions
// (applyKMResult, computeKMClassStandings, recalcKMElo, propagateKMResults,
// KM group/pair mutations) were interleaved among the React components and are
// a separate later pass; they now live elsewhere in src/. This module must
// NEVER import from the legacy artifact.
//
// The klubbmesterskapEngine wiring object + Padel24Engine.klubbmesterskap
// assignment live elsewhere (they mirror the booking-engine precedent).
//
// calcGroupStandings + createKlubbmesterskapTournament are pinned by
// tests/standings.test.ts — keep them behaviorally identical (byte-for-byte).
//
// loosely-shaped tournament / match / pair / division objects are typed as
// `any` on purpose.
// ============================================================

import { errMsg } from './errors.js';

export const KM_DEFAULT_GROUP_MATCH_FORMAT = 'bo3_super'; // best of 3 with super-tiebreak to 10
export const KM_DEFAULT_GROUP_MINUTES = 60;
export const KM_DEFAULT_KNOCKOUT_MINUTES = 75;
export const KM_DEFAULT_BUFFER_MINUTES = 5;

export const KM_MATCH_FORMATS = {
  bo3_super: {
    id: 'bo3_super',
    label: 'Best of 3 (super-tiebreak til 10 ved 1-1)',
    sets: 3,
    superTiebreak: true,
  },
  bo3_full: {
    id: 'bo3_full',
    label: 'Best of 3 (fullt 3. sett til 6)',
    sets: 3,
    superTiebreak: false,
  },
  bo1_set6: { id: 'bo1_set6', label: 'Best of 1 (ett sett til 6)', sets: 1, superTiebreak: false },
  bo1_short: {
    id: 'bo1_short',
    label: 'Kort sett til 4',
    sets: 1,
    superTiebreak: false,
    shortSet: true,
  },
};

// ---------- Game-for-game engine ----------
// Read from match.matchFormat. Each set is played game-for-game; the set is
// complete at 6 (or 4 in a short set) with a 2-game margin, or via tiebreak
// (7-6 / 5-4). In a deciding set (1-1 in best-of-3) a super-tiebreak to 10 is
// used if the format says so — otherwise a full set.
export function kmFormatSpec(matchFormat) {
  return KM_MATCH_FORMATS[matchFormat] || KM_MATCH_FORMATS.bo3_super;
}
export function kmSetTarget(fmtSpec, isDecider) {
  if (isDecider && fmtSpec.superTiebreak) return { target: 10, winBy: 2, isTb: true };
  if (fmtSpec.shortSet) return { target: 4, winBy: 2, cap: 5 };
  return { target: 6, winBy: 2, cap: 7 };
}
export function kmSetComplete(set, tgt) {
  const hi = Math.max(set.t1, set.t2),
    lo = Math.min(set.t1, set.t2);
  if (tgt.isTb) return hi >= tgt.target && hi - lo >= tgt.winBy;
  if (hi >= tgt.cap) return true; // 7-5, 7-6 (5-3, 5-4)
  if (hi >= tgt.target && hi - lo >= tgt.winBy) return true; // 6-0 … 6-4
  return false;
}
// Evaluates the whole set list. Returns set wins, whether the match is
// complete, and what target the current (unfinished) set has (normal/tiebreak).
export function kmEvaluateSets(setsArr, fmtSpec) {
  const needed = Math.ceil(fmtSpec.sets / 2);
  let w1 = 0,
    w2 = 0,
    completeIdx = -1;
  for (let i = 0; i < setsArr.length; i++) {
    const isDecider = w1 === needed - 1 && w2 === needed - 1;
    const tgt = kmSetTarget(fmtSpec, isDecider);
    if (kmSetComplete(setsArr[i], tgt)) {
      if (setsArr[i].t1 > setsArr[i].t2) w1++;
      else if (setsArr[i].t2 > setsArr[i].t1) w2++;
      if (w1 === needed || w2 === needed) {
        completeIdx = i;
        break;
      }
    }
  }
  const complete = w1 === needed || w2 === needed;
  const isDeciderNow = w1 === needed - 1 && w2 === needed - 1;
  return {
    setWins: { t1: w1, t2: w2 },
    complete,
    completeIdx,
    curTarget: kmSetTarget(fmtSpec, isDeciderNow),
    isDeciderTiebreak: !!kmSetTarget(fmtSpec, isDeciderNow).isTb,
    needed,
  };
}

export const KM_CLASS_TEMPLATES = {
  men: { id: 'men', label: 'Herrer', gender: 'M', defaultDivisions: ['A'] },
  women: { id: 'women', label: 'Damer', gender: 'F', defaultDivisions: ['A'] },
  mix: { id: 'mix', label: 'Mix', gender: 'mix', defaultDivisions: ['A'] },
  open: { id: 'open', label: 'Åpen', gender: 'any', defaultDivisions: ['A'] },
};

// ---------- 1a) Determine optimal group sizes ----------
// determineGroupSizes — determines the optimal distribution of pairs into groups.
// Rules (in priority order):
//   1. Preferably all groups of 4 (even distribution, 6 matches per group = ideal)
//   2. If not even: as many groups of 4 as possible, compensate with groups of 5
//   3. As a last resort: groups of 3 (gives only 3 matches per group, poor experience)
//
// Examples:
//   8  -> [4, 4]            (perfect)
//   12 -> [4, 4, 4]         (perfect)
//   13 -> [5, 4, 4]         (one group of 5, the rest of 4)
//   14 -> [5, 5, 4]         (two groups of 5)
//   15 -> [5, 5, 5]         (all of 5)
//   16 -> [4, 4, 4, 4]      (perfect)
//   10 -> [5, 5]            (two groups of 5)
//   9  -> [5, 4]            (one of 5, one of 4)
//
//   7  -> [4, 3]            two groups — DECIDED, #882 (see below)
//
// BELOW `minPairsForGroupStage` (default KM_MIN_PAIRS_FOR_GROUP_STAGE = 7) NONE
// OF THE ABOVE APPLIES — the guard below returns a single group of everybody:
//   6  -> [6]               one group, all-play-all — small category
//
// SEVEN PAIRS ARE TWO GROUPS, 4 + 3 (#882, decided by the CTO 2026-10-18). Until
// then the threshold was 8 and seven pairs played one group of seven, while this
// doc block claimed `7 -> [4, 3]` (#496 corrected the comment, not the code).
// The owner chose two groups, so the threshold moved to 7: seven now takes the
// «allow a group of three» step below, and nothing else in the size table moves.
// Pinned by «DECIDED (#882): seven pairs are two groups, 4 + 3».
//
// The setup wizard reads the same constant to decide which classes are «small»
// (one group, with a per-class format chooser), so the screen and the engine
// cannot disagree about where the line is.
export const KM_MIN_PAIRS_FOR_GROUP_STAGE = 7;

export function determineGroupSizes(pairCount, opts: any = {}) {
  const maxSize = opts.maxSize || 5;
  const _preferredSize = opts.preferredSize || 4;
  const minPairsForGroupStage = opts.minPairsForGroupStage || KM_MIN_PAIRS_FOR_GROUP_STAGE;

  if (pairCount < 2) return [];
  if (pairCount < minPairsForGroupStage) {
    // Too few pairs: a single group (all-play-all, small-category format)
    return [pairCount];
  }

  // Step 1: try combinations of only groups of 4 and 5
  let best = null;
  for (let fives = 0; fives * 5 <= pairCount; fives++) {
    const rest = pairCount - fives * 5;
    if (rest % 4 === 0) {
      const fours = rest / 4;
      const total = fours + fives;
      // Prefer the most groups of 4, then the fewest groups overall
      if (!best || fours > best.fours || (fours === best.fours && total < best.total)) {
        best = { fours, fives, total };
      }
    }
  }

  if (best) {
    const sizes = [];
    for (let i = 0; i < best.fives; i++) sizes.push(5);
    for (let i = 0; i < best.fours; i++) sizes.push(4);
    return sizes;
  }

  // Step 2: allow groups of 3 (prefer few groups of 3 and many groups of 4)
  best = null;
  for (let threes = 1; threes * 3 <= pairCount; threes++) {
    for (let fives = 0; fives * 5 + threes * 3 <= pairCount; fives++) {
      const rest = pairCount - fives * 5 - threes * 3;
      if (rest >= 0 && rest % 4 === 0) {
        const fours = rest / 4;
        const total = fours + fives + threes;
        if (
          !best ||
          threes < best.threes ||
          (threes === best.threes && fours > best.fours) ||
          (threes === best.threes && fours === best.fours && total < best.total)
        ) {
          best = { fours, fives, threes, total };
        }
      }
    }
  }

  if (best) {
    const sizes = [];
    for (let i = 0; i < best.fives; i++) sizes.push(5);
    for (let i = 0; i < best.fours; i++) sizes.push(4);
    for (let i = 0; i < best.threes; i++) sizes.push(3);
    return sizes;
  }

  // Last fallback: even distribution
  const gc = Math.max(1, Math.ceil(pairCount / maxSize));
  const base = Math.floor(pairCount / gc);
  const extra = pairCount - base * gc;
  const sizes = [];
  for (let i = 0; i < extra; i++) sizes.push(base + 1);
  for (let i = 0; i < gc - extra; i++) sizes.push(base);
  return sizes;
}

// ---------- 1b) Determine knockout structure based on the number of groups ----------
// Returns { advanceTopN, bracketSize, luckyLosers, format, formatLabel }
//
// Philosophy:
//   - Each group winner should preferably advance directly (most prestige)
//   - Top 2 from each group when the number of groups is "clean" (2, 4, 8, 16)
//   - Lucky losers fill up to the next power of 2 when the number of groups is odd
//
// override: 'auto' (default) | 'semifinals' | 'quarters' | 'r16' — admin can
//   force a specific knockout format per class. The bracket size follows the
//   format (4/8/16), and top-N + lucky losers are computed from the number of groups.
export function determineBracketStructure(groupCount, _pairCount = 0, override = 'auto') {
  if (groupCount <= 1) {
    // One group (a small class, under KM_MIN_PAIRS_FOR_GROUP_STAGE pairs): the
    // group winner is champion by default,
    // but the operator can FORCE a knockout on top of the round robin —
    // semi-finals (top 4 → SF + F) or just a final (top 2).
    if (override === 'semifinals' && _pairCount >= 4) {
      return {
        advanceTopN: 4,
        bracketSize: 4,
        luckyLosers: 0,
        format: 'semifinals',
        formatLabel: 'Semifinaler (topp 4 fra gruppen)',
      };
    }
    if (override === 'final' && _pairCount >= 2) {
      return {
        advanceTopN: 2,
        bracketSize: 2,
        luckyLosers: 0,
        format: 'final',
        formatLabel: 'Finale (topp 2 fra gruppen)',
      };
    }
    return {
      advanceTopN: 0,
      bracketSize: 0,
      luckyLosers: 0,
      format: 'group_winner',
      formatLabel: 'Gruppe-vinner blir mester',
    };
  }
  // Forced format: compute top-N and lucky losers so the bracket is filled
  if (override && override !== 'auto') {
    const sizeFor = { semifinals: 4, quarters: 8, r16: 16, r32: 32 };
    const labelFor = {
      semifinals: 'Semifinaler',
      quarters: 'Kvartfinaler',
      r16: 'Åttendedelsfinaler',
      r32: 'Sekstendedelsfinaler',
    };
    const bracketSize = sizeFor[override];
    if (bracketSize) {
      if (groupCount > bracketSize) {
        // More groups than slots → only the best group winners, the rest are cut
        return {
          advanceTopN: 1,
          bracketSize,
          luckyLosers: 0,
          format: `${override}_tight`,
          formatLabel: `${labelFor[override]} (for mange grupper — kun ${bracketSize} beste vinnere)`,
          overflow: true,
        };
      }
      // Choose the HIGHEST possible number advancing per group (max 2), and fill
      // the rest with lucky losers. This gives "top 2 + few LL" rather than "top 1 + many LL".
      const advanceTopN = Math.min(2, Math.floor(bracketSize / groupCount));
      const luckyLosers = bracketSize - groupCount * advanceTopN;
      const llTxt =
        luckyLosers > 0 ? ` + ${luckyLosers} lucky loser${luckyLosers === 1 ? '' : 's'}` : '';
      return {
        advanceTopN,
        bracketSize,
        luckyLosers,
        format: luckyLosers > 0 ? `${override}_ll` : override,
        formatLabel: `${labelFor[override]} (topp ${advanceTopN} fra hver gruppe${llTxt})`,
      };
    }
  }
  if (groupCount === 2) {
    return {
      advanceTopN: 2,
      bracketSize: 4,
      luckyLosers: 0,
      format: 'semifinals',
      formatLabel: 'Semifinaler (topp 2 fra hver gruppe)',
    };
  }
  if (groupCount === 3) {
    return {
      advanceTopN: 1,
      bracketSize: 4,
      luckyLosers: 1,
      format: 'semifinals_ll',
      formatLabel: 'Semifinaler (3 gruppe-vinnere + 1 lucky loser)',
    };
  }
  if (groupCount === 4) {
    return {
      advanceTopN: 2,
      bracketSize: 8,
      luckyLosers: 0,
      format: 'quarters',
      formatLabel: 'Kvartfinaler (topp 2 fra hver gruppe)',
    };
  }
  if (groupCount >= 5 && groupCount <= 7) {
    return {
      advanceTopN: 1,
      bracketSize: 8,
      luckyLosers: 8 - groupCount,
      format: 'quarters_ll',
      formatLabel: `Kvartfinaler (${groupCount} gruppe-vinnere + ${8 - groupCount} lucky losers)`,
    };
  }
  if (groupCount === 8) {
    return {
      advanceTopN: 2,
      bracketSize: 16,
      luckyLosers: 0,
      format: 'r16',
      formatLabel: 'Åttendedelsfinaler (topp 2 fra hver gruppe)',
    };
  }
  if (groupCount >= 9 && groupCount <= 15) {
    return {
      advanceTopN: 1,
      bracketSize: 16,
      luckyLosers: 16 - groupCount,
      format: 'r16_ll',
      formatLabel: `Åttendedelsfinaler (${groupCount} gruppe-vinnere + ${16 - groupCount} lucky losers)`,
    };
  }
  if (groupCount === 16) {
    return {
      advanceTopN: 2,
      bracketSize: 32,
      luckyLosers: 0,
      format: 'r32',
      formatLabel: 'Sekstendedelsfinaler (topp 2 fra hver gruppe)',
    };
  }
  // 17+: 32-bracket with LL
  const bracketSize = 32;
  return {
    advanceTopN: 1,
    bracketSize,
    luckyLosers: bracketSize - groupCount,
    format: 'r32_ll',
    formatLabel: `Sekstendedelsfinaler (${groupCount} gruppe-vinnere + ${bracketSize - groupCount} lucky losers)`,
  };
}

// ---------- 1) Snake / serpentine seeding ----------
// Distributes pairs into groups with a snake pattern. pairs MUST be sorted best first.
// The second argument can be either:
//   - a number (groupCount): uses even distribution
//   - an array (groupSizes): uses specific sizes per group
// ── GROUPS ARE NAMED WITH LETTERS, EVERYWHERE (#882) ─────────────────
//
// All three seeders below name groups «A», «B», «C» (`kmGroupLetter`), the same
// convention as the manual path in `createKlubbmesterskapTournament` and as
// `addGroupInTournament` / `deleteGroupInTournament` in kmResults.ts.
//
// WHY ONE CONVENTION MATTERS. Until #882 these seeders wrote «1», «2», «3» while
// the group editors wrote letters, so one tournament could hold both after a
// group was deleted or added. `rebuildKnockoutForDivision` orders groups by
// `name.localeCompare`, where digits sort before letters, and that order decides
// which group's winner meets which. The CTO decided letters, everywhere
// (2026-10-18). Pinned by «DECIDED (#882): every seeder names groups with
// LETTERS».
//
// STORED TOURNAMENTS ARE NOT RENAMED. A tournament drawn before #882 keeps the
// numbers it stored; a redraw maps onto the existing group objects and keeps
// their names. What a screen PRINTS is a separate question: most KM screens show
// a group through `kmGroupNumber`, which turns «A» into «1».
export function kmGroupLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

export function snakeSeedGroups(pairs, groupCountOrSizes) {
  if (!Array.isArray(pairs) || pairs.length === 0) return [];

  // Normalize to a groupSizes array
  let groupSizes;
  if (Array.isArray(groupCountOrSizes)) {
    groupSizes = groupCountOrSizes;
  } else {
    const gc = Math.max(1, groupCountOrSizes || 1);
    // Even distribution — the last group(s) may be larger
    const base = Math.floor(pairs.length / gc);
    const extra = pairs.length - base * gc;
    groupSizes = [];
    for (let i = 0; i < extra; i++) groupSizes.push(base + 1);
    for (let i = 0; i < gc - extra; i++) groupSizes.push(base);
  }

  const groupCount = groupSizes.length;
  const groups = Array.from({ length: groupCount }, (_, i) => ({
    id: `grp_${kmGroupLetter(i).toLowerCase()}`,
    name: kmGroupLetter(i),
    pairs: [],
    capacity: groupSizes[i],
  }));

  // Snake distribution respecting capacity:
  // Build a round order that alternates direction, and skip full groups.
  let i = 0;
  let round = 0;
  while (i < pairs.length) {
    const order =
      round % 2 === 0
        ? Array.from({ length: groupCount }, (_, k) => k)
        : Array.from({ length: groupCount }, (_, k) => groupCount - 1 - k);

    let placedThisRound = 0;
    for (const gi of order) {
      if (groups[gi].pairs.length < groups[gi].capacity) {
        if (i >= pairs.length) break;
        groups[gi].pairs.push({ ...pairs[i], seed: i + 1 });
        i++;
        placedThisRound++;
      }
    }
    if (placedThisRound === 0) break; // all groups full, but more pairs left (does not happen with a correct calculation)
    round++;
  }

  return groups;
}

// seedGroupsWithRule — PARTIAL seeding: the top `seedPerGroup` pairs per group
// are placed deterministically via snake (even strength distribution), the rest are
// drawn RANDOMLY into the empty slots. Gives a new result every time for the
// un-seeded part. seedPerGroup=null/large → equivalent to a full snake. Returns the
// same shape as snakeSeedGroups (objects with .pairs).
// ── THE ORIGINAL SEED TRAVELS WITH THE PAIR, AS ON THE OTHER TWO (#882) ──
//
// `snakeSeedGroups` and the block seeder both write `seed: n` onto each pair,
// where n is the pair's place in the seeded input list. This one now does the
// same, and the seed is the ORIGINAL place — a pair drawn at random into group A
// keeps the seed it arrived with, not its position after the shuffle. That is
// what `calcGroupStandings`'s last tiebreak `(a.pair.seed || 999)` reads.
//
// Until #882 this seeder stamped nothing, so a pair that arrived without a seed
// left without one and compared equal at 999 in that tiebreak. The CTO decided
// to keep the seed (2026-10-18). Pinned by «DECIDED (#882): the seeded-rule draw
// stamps the ORIGINAL seed». Stamping changes no draw: the shuffle consumes the
// same random numbers in the same order.
export function seedGroupsWithRule(inputPairs, groupSizes, seedPerGroup) {
  if (!Array.isArray(inputPairs) || inputPairs.length === 0) return [];
  const pairs = inputPairs.map((p, i) => ({ ...p, seed: i + 1 }));
  const sizes = Array.isArray(groupSizes) ? groupSizes : [pairs.length];
  const groupCount = sizes.length;
  const groups = sizes.map((cap, i) => ({
    id: `grp_${kmGroupLetter(i).toLowerCase()}`,
    name: kmGroupLetter(i),
    pairs: [],
    capacity: cap,
  }));
  const spg = Math.max(1, seedPerGroup || 1);
  const seededTotal = Math.min(pairs.length, spg * groupCount);
  const seeded = pairs.slice(0, seededTotal);
  const rest = pairs.slice(seededTotal);
  // Snake-distribute the seeded ones (max spg per group, respect capacity)
  let idx = 0,
    round = 0;
  while (idx < seeded.length) {
    const order =
      round % 2 === 0 ? groups.map((_, k) => k) : groups.map((_, k) => groupCount - 1 - k);
    let placed = 0;
    for (const gi of order) {
      const cap = Math.min(spg, groups[gi].capacity);
      if (groups[gi].pairs.length < cap) {
        if (idx >= seeded.length) break;
        groups[gi].pairs.push({ ...seeded[idx] });
        idx++;
        placed++;
      }
    }
    if (placed === 0) break;
    round++;
  }
  // Shuffle the rest and fill the empty slots
  const shuffled = [...rest];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = shuffled[i];
    shuffled[i] = shuffled[j];
    shuffled[j] = t;
  }
  let r = 0;
  for (const g of groups) {
    while (g.pairs.length < g.capacity && r < shuffled.length) {
      g.pairs.push({ ...shuffled[r] });
      r++;
    }
  }
  return groups;
}
// Group A gets seeds 1..N, group B the next N, etc. (the strongest concentrated in group A).
// Returns the same shape as snakeSeedGroups (objects with .pairs).
export function sequentialSeedGroups(pairs, groupCountOrSizes) {
  if (!Array.isArray(pairs) || pairs.length === 0) return [];
  let groupSizes;
  if (Array.isArray(groupCountOrSizes)) {
    groupSizes = groupCountOrSizes;
  } else {
    const gc = Math.max(1, groupCountOrSizes || 1);
    const base = Math.floor(pairs.length / gc);
    const extra = pairs.length - base * gc;
    groupSizes = [];
    for (let i = 0; i < extra; i++) groupSizes.push(base + 1);
    for (let i = 0; i < gc - extra; i++) groupSizes.push(base);
  }
  const groups = groupSizes.map((cap, i) => ({
    id: `grp_${kmGroupLetter(i).toLowerCase()}`,
    name: kmGroupLetter(i),
    pairs: [],
    capacity: cap,
  }));
  let idx = 0;
  for (const g of groups) {
    for (let k = 0; k < g.capacity && idx < pairs.length; k++) {
      g.pairs.push({ ...pairs[idx], seed: idx + 1 });
      idx++;
    }
  }
  return groups;
}

// Returns an array of rounds; each round has an array of matches.
// Each group gets n*(n-1)/2 matches, balanced per round.
export function roundRobinSchedule(pairs) {
  const n = pairs?.length || 0;
  if (n < 2) return [];

  const items = [...pairs];
  if (n % 2 === 1) items.push(null); // bye for an odd count
  const sz = items.length;

  const rounds = [];
  let arr = items.slice();
  for (let r = 0; r < sz - 1; r++) {
    const matches = [];
    for (let i = 0; i < sz / 2; i++) {
      const a = arr[i];
      const b = arr[sz - 1 - i];
      if (a && b) matches.push({ p1: a, p2: b });
    }
    rounds.push(matches);
    // Rotate, KEEPING INDEX 0 FIXED — the standard circle method.
    //
    // A consequence worth naming (#496): because `arr[0]` never moves, the first
    // pair is `p1` in every round it plays. For a league that means a permanent
    // «home» side — cosmetic for court and time, but score orientation follows
    // p1/p2, so it is always entered the same way round for that pair. Pinned by
    // «CHARACTERIZED: the first pair is always team1 — a permanent home
    // advantage».
    const fixed = arr[0];
    const rest = arr.slice(1);
    rest.unshift(rest.pop());
    arr = [fixed, ...rest];
  }
  return rounds;
}

// ---------- 3) Cross-over from groups to knockout ----------
// Returns an array of "placeholder" matches. Each match has p1/p2 pointing to
// "group-X-pos-N" or "ll-N" (lucky loser). These are filled in when the group
// stage is finished.
//
// Supports:
//   - 2 groups × 2 advance = 4 teams (SF)
//   - 3 groups × 1 + 1 LL = 4 teams (SF)
//   - 4 groups × 2 advance = 8 teams (QF)
//   - 5-7 groups × 1 + LL = 8 teams (QF)
//   - 8 groups × 2 advance = 16 teams (R16)
//   - 9-15 groups × 1 + LL = 16 teams (R16)
//   - 16 groups × 2 advance = 32 teams (R32)
export function buildCrossOverBracket(groups, advanceTopN, options: any = {}) {
  const nGroups = groups.length;
  const luckyLosers = options.luckyLosers || 0;
  const bracketSize = options.bracketSize || nGroups * advanceTopN;

  if (nGroups === 1) {
    // Single group: only a FORCED knockout (division.bracketSize 4 or 2) — the
    // default is «group winner is champion». Seeded from the group table:
    // 1 v 4 and 2 v 3 for semi-finals, 1 v 2 for a straight final.
    const g = groups[0];
    const pos = (p) => ({ from: 'group', groupId: g.id, groupName: g.name, pos: p });
    const forced = options.bracketSize || 0;
    if (forced === 4 && advanceTopN >= 4) {
      return [
        { round: 'sf', slot: 1, p1: pos(1), p2: pos(4) },
        { round: 'sf', slot: 2, p1: pos(2), p2: pos(3) },
      ];
    }
    if (forced === 2 && advanceTopN >= 2) {
      return [{ round: 'f', slot: 1, p1: pos(1), p2: pos(2) }];
    }
    return []; // no knockout needed
  }

  // Classic cross-over patterns (2/4/8 groups × 2 advance)
  if (advanceTopN === 2 && nGroups === 2) {
    return [
      {
        round: 'sf',
        slot: 1,
        p1: { from: 'group', groupId: groups[0].id, groupName: groups[0].name, pos: 1 },
        p2: { from: 'group', groupId: groups[1].id, groupName: groups[1].name, pos: 2 },
      },
      {
        round: 'sf',
        slot: 2,
        p1: { from: 'group', groupId: groups[1].id, groupName: groups[1].name, pos: 1 },
        p2: { from: 'group', groupId: groups[0].id, groupName: groups[0].name, pos: 2 },
      },
    ];
  }

  if (advanceTopN === 2 && nGroups === 4) {
    return [
      {
        round: 'qf',
        slot: 1,
        p1: { from: 'group', groupId: groups[0].id, groupName: groups[0].name, pos: 1 },
        p2: { from: 'group', groupId: groups[1].id, groupName: groups[1].name, pos: 2 },
      },
      {
        round: 'qf',
        slot: 2,
        p1: { from: 'group', groupId: groups[2].id, groupName: groups[2].name, pos: 1 },
        p2: { from: 'group', groupId: groups[3].id, groupName: groups[3].name, pos: 2 },
      },
      {
        round: 'qf',
        slot: 3,
        p1: { from: 'group', groupId: groups[1].id, groupName: groups[1].name, pos: 1 },
        p2: { from: 'group', groupId: groups[0].id, groupName: groups[0].name, pos: 2 },
      },
      {
        round: 'qf',
        slot: 4,
        p1: { from: 'group', groupId: groups[3].id, groupName: groups[3].name, pos: 1 },
        p2: { from: 'group', groupId: groups[2].id, groupName: groups[2].name, pos: 2 },
      },
    ];
  }

  if (advanceTopN === 2 && nGroups === 8) {
    return [
      {
        round: 'r16',
        slot: 1,
        p1: { from: 'group', groupId: groups[0].id, groupName: groups[0].name, pos: 1 },
        p2: { from: 'group', groupId: groups[7].id, groupName: groups[7].name, pos: 2 },
      },
      {
        round: 'r16',
        slot: 2,
        p1: { from: 'group', groupId: groups[3].id, groupName: groups[3].name, pos: 1 },
        p2: { from: 'group', groupId: groups[4].id, groupName: groups[4].name, pos: 2 },
      },
      {
        round: 'r16',
        slot: 3,
        p1: { from: 'group', groupId: groups[5].id, groupName: groups[5].name, pos: 1 },
        p2: { from: 'group', groupId: groups[2].id, groupName: groups[2].name, pos: 2 },
      },
      {
        round: 'r16',
        slot: 4,
        p1: { from: 'group', groupId: groups[6].id, groupName: groups[6].name, pos: 1 },
        p2: { from: 'group', groupId: groups[1].id, groupName: groups[1].name, pos: 2 },
      },
      {
        round: 'r16',
        slot: 5,
        p1: { from: 'group', groupId: groups[1].id, groupName: groups[1].name, pos: 1 },
        p2: { from: 'group', groupId: groups[6].id, groupName: groups[6].name, pos: 2 },
      },
      {
        round: 'r16',
        slot: 6,
        p1: { from: 'group', groupId: groups[2].id, groupName: groups[2].name, pos: 1 },
        p2: { from: 'group', groupId: groups[5].id, groupName: groups[5].name, pos: 2 },
      },
      {
        round: 'r16',
        slot: 7,
        p1: { from: 'group', groupId: groups[4].id, groupName: groups[4].name, pos: 1 },
        p2: { from: 'group', groupId: groups[3].id, groupName: groups[3].name, pos: 2 },
      },
      {
        round: 'r16',
        slot: 8,
        p1: { from: 'group', groupId: groups[7].id, groupName: groups[7].name, pos: 1 },
        p2: { from: 'group', groupId: groups[0].id, groupName: groups[0].name, pos: 2 },
      },
    ];
  }

  // ──── Cases with lucky losers ────
  // Build a list of "advancers": group winners first, then LL placeholders
  if (advanceTopN === 1 && luckyLosers > 0) {
    return buildBracketWithLuckyLosers(groups, luckyLosers, bracketSize);
  }

  // 16 groups × top 2 = R32 (same pattern as 8 groups × R16)
  if (advanceTopN === 2 && nGroups === 16) {
    // Generalized: split into two halves, each half mirrors the other
    const matches = [];
    for (let i = 0; i < nGroups; i++) {
      const oppI = nGroups - 1 - i;
      matches.push({
        round: 'r32',
        slot: i + 1,
        p1: { from: 'group', groupId: groups[i].id, groupName: groups[i].name, pos: 1 },
        p2: { from: 'group', groupId: groups[oppI].id, groupName: groups[oppI].name, pos: 2 },
      });
    }
    return matches.slice(0, nGroups); // 16 matches
  }

  throw new Error(
    `buildCrossOverBracket: ${nGroups} grupper × ${advanceTopN} advance + ${luckyLosers} LL ikke støttet`,
  );
}

// Build a bracket where group winners + N lucky losers fill a `bracketSize` bracket.
// The group winners get priority: seeds 1..groupCount based on group order (A=1, B=2, etc.)
// The LLs get seeds groupCount+1..bracketSize.
//
// Placement in the bracket uses the standard tennis seed pattern:
//   8 teams: [1,8,5,4,3,6,7,2] (slot 1..8)
//   16 teams: [1,16,9,8,5,12,13,4,3,14,11,6,7,10,15,2]
export function buildBracketWithLuckyLosers(groups, luckyLosers, bracketSize) {
  const nGroups = groups.length;
  if (nGroups + luckyLosers !== bracketSize) {
    throw new Error(
      `buildBracketWithLuckyLosers: ${nGroups} grupper + ${luckyLosers} LL ≠ ${bracketSize} bracket`,
    );
  }

  // Build a list of "advancers" — references that are later filled with actual pairs
  const advancers = [];
  // Group winners (seeds 1..nGroups, ranked by group order)
  for (let i = 0; i < nGroups; i++) {
    advancers.push({
      from: 'group',
      groupId: groups[i].id,
      groupName: groups[i].name,
      pos: 1,
      seed: i + 1, // group A = seed 1, group B = seed 2, etc.
    });
  }
  // Lucky losers (seeds nGroups+1..bracketSize)
  for (let i = 0; i < luckyLosers; i++) {
    advancers.push({
      from: 'lucky_loser',
      llRank: i + 1, // LL #1 is the best lucky loser
      seed: nGroups + i + 1,
    });
  }

  // Standard bracket-slot mapping for different sizes
  // Each slot points to a seed (1-indexed).
  const SEED_TO_SLOT = {
    4: [1, 4, 3, 2],
    8: [1, 8, 5, 4, 3, 6, 7, 2],
    16: [1, 16, 9, 8, 5, 12, 13, 4, 3, 14, 11, 6, 7, 10, 15, 2],
    32: [
      1, 32, 17, 16, 9, 24, 25, 8, 5, 28, 21, 12, 13, 20, 29, 4, 3, 30, 19, 14, 11, 22, 27, 6, 7,
      26, 23, 10, 15, 18, 31, 2,
    ],
  };
  const seedToSlot = SEED_TO_SLOT[bracketSize];
  if (!seedToSlot)
    throw new Error(`buildBracketWithLuckyLosers: bracket-størrelse ${bracketSize} ikke støttet`);

  // Round name
  const roundName =
    bracketSize === 32 ? 'r32' : bracketSize === 16 ? 'r16' : bracketSize === 8 ? 'qf' : 'sf';

  // Place each seed in its slot
  const slots = new Array(bracketSize);
  for (let i = 0; i < bracketSize; i++) {
    const seedAtThisSlot = seedToSlot[i];
    slots[i] = advancers.find((a) => a.seed === seedAtThisSlot);
  }

  // Build matches: slot 1 vs slot 2, slot 3 vs slot 4, etc.
  const matches = [];
  for (let i = 0; i < bracketSize / 2; i++) {
    matches.push({
      round: roundName,
      slot: i + 1,
      p1: slots[i * 2],
      p2: slots[i * 2 + 1],
    });
  }
  return matches;
}

// Build a single-elim tree from a set of "advancers" (group winners or similar)
export function buildBracketFromAdvancers(advancers) {
  const n = advancers.length;
  if (n < 2) return [];
  // Assumes n is a power of 2. Rounds down if not.
  let size = 1;
  while (size * 2 <= n) size *= 2;
  const trimmed = advancers.slice(0, size);
  const roundName = size === 16 ? 'r16' : size === 8 ? 'qf' : size === 4 ? 'sf' : 'f';
  const out = [];
  for (let i = 0; i < trimmed.length / 2; i++) {
    out.push({
      round: roundName,
      slot: i + 1,
      p1: { from: 'group', ...trimmed[i] },
      p2: { from: 'group', ...trimmed[trimmed.length - 1 - i] },
    });
  }
  return out;
}

// ---------- 4) FIP-style bracket seeding (pure cup, no group stage) ----------
export function fipBracketSeed(pairs) {
  const n = pairs?.length || 0;
  // Standard tennis bracket seeding patterns (1-indexed):
  const positions = {
    4: [1, 4, 3, 2],
    8: [1, 8, 5, 4, 3, 6, 7, 2],
    16: [1, 16, 9, 8, 5, 12, 13, 4, 3, 14, 11, 6, 7, 10, 15, 2],
    32: [
      1, 32, 17, 16, 9, 24, 25, 8, 5, 28, 21, 12, 13, 20, 29, 4, 3, 30, 19, 14, 11, 22, 27, 6, 7,
      26, 23, 10, 15, 18, 31, 2,
    ],
  };
  if (n < 2) throw new Error(`fipBracketSeed: ${n} par er for få (minst 2)`);
  if (n > 32) throw new Error(`fipBracketSeed: ${n} par støttes ikke (maks 32)`);
  // A real draw is rarely an exact power of two. Round UP to the next bracket size
  // and leave the surplus slots empty — those become BYES, so the top seeds get a
  // free pass to the next round (standard FIP/tennis practice). Before this, any
  // count that was not exactly 4/8/16/32 threw, the caller swallowed the error and
  // returned NO matches at all — a 5-pair division silently vanished from the
  // schedule and the calendar. `null` as a bye matches the round-robin scheduler.
  const size = [4, 8, 16, 32].find((s) => s >= n);
  const order = positions[size];
  // seed > n → no such pair → bye. For an exact 4/8/16/32 draw nothing is padded,
  // so those brackets are byte-for-byte what they were before.
  return order.map((seed) => (seed <= n ? pairs[seed - 1] : null));
}

// ---------- 5) Group standings ----------
// FIP/padel-standard ranking. rankingOrder is an optional array of
// { id, enabled } that determines the tiebreak order (wins, head-to-head,
// set diff, games diff, games won). Default follows FIP.
export function calcGroupStandings(groupPairs, matches, rankingOrder = null) {
  const stats: any = {};
  groupPairs.forEach((p) => {
    stats[p.id] = {
      pair: p,
      played: 0,
      wins: 0,
      losses: 0,
      points: 0,
      setsFor: 0,
      setsAgainst: 0,
      setDiff: 0,
      gamesFor: 0,
      gamesAgainst: 0,
      gameDiff: 0,
      headToHead: {}, // pairId -> { wins, losses }
    };
  });

  (matches || []).forEach((m) => {
    if (!m.score || m.score.t1 == null || m.score.t2 == null) return;
    const p1Id = m.team1?.pairId;
    const p2Id = m.team2?.pairId;
    if (!p1Id || !p2Id || !stats[p1Id] || !stats[p2Id]) return;

    const s1 = stats[p1Id],
      s2 = stats[p2Id];
    s1.played++;
    s2.played++;
    s1.setsFor += m.score.t1;
    s1.setsAgainst += m.score.t2;
    s2.setsFor += m.score.t2;
    s2.setsAgainst += m.score.t1;
    // Game diff from the detailed score if available
    if (Array.isArray(m.score.sets)) {
      m.score.sets.forEach((set) => {
        s1.gamesFor += set.t1 || 0;
        s1.gamesAgainst += set.t2 || 0;
        s2.gamesFor += set.t2 || 0;
        s2.gamesAgainst += set.t1 || 0;
      });
    }
    if (m.score.t1 > m.score.t2) {
      s1.wins++;
      s1.points += 3;
      s2.losses++;
      s1.headToHead[p2Id] = { result: 'win' };
      s2.headToHead[p1Id] = { result: 'loss' };
    } else if (m.score.t2 > m.score.t1) {
      s2.wins++;
      s2.points += 3;
      s1.losses++;
      s2.headToHead[p1Id] = { result: 'win' };
      s1.headToHead[p2Id] = { result: 'loss' };
    }
  });

  Object.values(stats).forEach((s: any) => {
    s.setDiff = s.setsFor - s.setsAgainst;
    s.gameDiff = s.gamesFor - s.gamesAgainst;
  });

  // Build a list of tiebreak comparators from rankingOrder. Each criterion
  // returns a number where higher = better (sorted descending). Head-to-head
  // is handled specially (it requires the two teams).
  const enabledOrder =
    Array.isArray(rankingOrder) && rankingOrder.length > 0
      ? rankingOrder.filter((r) => r.enabled)
      : [
          { id: 'wins' },
          { id: 'h2h' },
          { id: 'setsDiff' },
          { id: 'gamesDiff' },
          { id: 'gamesWon' },
        ];

  const compareBy = (id, a, b) => {
    switch (id) {
      case 'wins':
        return b.points - a.points || b.wins - a.wins;
      case 'setsDiff':
        return b.setDiff - a.setDiff;
      case 'gamesDiff':
        return b.gameDiff - a.gameDiff;
      case 'gamesWon':
        return b.gamesFor - a.gamesFor;
      case 'h2h': {
        const h = a.headToHead?.[b.pair.id];
        if (h?.result === 'win') return -1;
        if (h?.result === 'loss') return 1;
        return 0;
      }
      default:
        return 0;
    }
  };

  const sorted = Object.values(stats).sort((a: any, b: any) => {
    for (const crit of enabledOrder) {
      const d = compareBy(crit.id, a, b);
      if (d !== 0) return d;
    }
    return (a.pair.seed || 999) - (b.pair.seed || 999);
  });

  return sorted.map((s: any, i: number) => ({ ...s, pos: i + 1 }));
}

// ---------- 6) Time budget ----------
// ── BRONZE FINAL: OFF UNLESS SOMEBODY SWITCHES IT ON (#882) ──────────
//
// The CTO confirmed on 2026-10-18 that a bronze final is OFF when nobody has
// switched it on or off. The generator (`buildSubsequentARounds`) has always
// read it that way — `division.bronzeFinal === true` — but three COUNTERS of
// matches assumed the opposite and added a bronze final the generator never
// builds: `calculateKMMatchCount`, `simulateKMCapacity` and the knockout panel's
// «Sluttspill-kamper». They now share these two helpers with the generator, so a
// time estimate counts the matches the tournament will actually contain.
//
// A stored division that says `bronzeFinal: true` keeps its bronze final, one
// that says `false` keeps none, and one that never stored the key has none —
// which is what it already had.
export function kmBronzeFinalOn(cfg: any): boolean {
  return cfg?.bronzeFinal === true;
}
/** Matches in a single-elimination bracket of `bracketSize`, plus the bronze
 *  final when it is on. A bronze final needs two semi-finals, so a bracket under
 *  four never gets one. */
export function kmKnockoutMatchCount(bracketSize: number, bronzeFinal: boolean): number {
  if (!(bracketSize >= 2)) return 0;
  return bracketSize - 1 + (bronzeFinal && bracketSize >= 4 ? 1 : 0);
}

export function calculateKMMatchCount({
  groupCount,
  pairsPerGroup,
  advanceTopN,
  withConsolation,
  withBronze = false,
}: any) {
  if (!groupCount || !pairsPerGroup)
    return { groupMatches: 0, aBracketMatches: 0, bBracketMatches: 0, total: 0 };
  const groupMatches = groupCount * ((pairsPerGroup * (pairsPerGroup - 1)) / 2);
  const advancing = groupCount * (advanceTopN || 2);
  let aBracketMatches = 0;
  if (advancing >= 2) {
    let bSize = 1;
    while (bSize * 2 <= advancing) bSize *= 2;
    aBracketMatches = kmKnockoutMatchCount(bSize, withBronze === true); // single elim (+ bronze when on)
  }
  let bBracketMatches = 0;
  if (withConsolation) {
    const losing = groupCount * pairsPerGroup - advancing;
    if (losing >= 2) {
      let bSize = 1;
      while (bSize * 2 <= losing) bSize *= 2;
      bBracketMatches = bSize - 1;
    }
  }
  return {
    groupMatches,
    aBracketMatches,
    bBracketMatches,
    total: groupMatches + aBracketMatches + bBracketMatches,
  };
}

export function calculateKMTimeBudget({ matchCount, courts, minutesPerMatch, bufferMinutes }) {
  if (!matchCount || !courts) return { slots: 0, totalMinutes: 0, hoursDecimal: 0 };
  const c = Math.max(1, courts);
  const slots = Math.ceil(matchCount / c);
  const buf = bufferMinutes || 0;
  const totalMinutes = slots * (minutesPerMatch + buf) - buf;
  return { slots, totalMinutes, hoursDecimal: totalMinutes / 60 };
}

// Rough estimate of how many pairs in total there is room for in a KM, given
// days/courts/times. Assumes 4 pairs per group, top 2 advancing, single elim
// without a B-knockout. Per pair: ~1.5 group matches (6 matches spread across 4
// pairs) + ~0.5 knockout matches (2 of 4). Group stage and knockout have
// different match durations (match format) — so they are weighted separately.
export function estimateKMPairCapacity({
  days = [],
  defaultCourts = 4,
  bufferMinutes = 0,
  groupMatchMinutes = 60,
  knockoutMatchMinutes = 75,
}) {
  let courtMinutes = 0;
  days.forEach((d) => {
    if (!d.startTime || !d.endTime) return;
    if (d.reserved) return; // reserved days (A-finals only) are kept out of the pair estimate
    const [h1, m1] = d.startTime.split(':').map(Number);
    const [h2, m2] = d.endTime.split(':').map(Number);
    const dayMin = Math.max(0, h2 * 60 + m2 - (h1 * 60 + m1));
    const breakMin = (d.breaks || []).reduce((s, b) => s + (b.durationMin || 0), 0);
    const playable = Math.max(0, dayMin - breakMin);
    courtMinutes += playable * (d.courts || defaultCourts || 0);
  });
  const buf = bufferMinutes || 0;
  const timePerPair = 1.5 * (groupMatchMinutes + buf) + 0.5 * (knockoutMatchMinutes + buf);
  const maxPairs = timePerPair > 0 ? Math.floor(courtMinutes / timePerPair) : 0;
  return { courtMinutes, timePerPair, maxPairs };
}

// Smarter capacity estimator: simulates match placement per class to give
// a more realistic estimate than the naive estimateKMPairCapacity. Takes into account:
//   • Number of classes and how many pairs in each
//   • Group-stage size (actual number of round-robin matches)
//   • Knockout-bracket size (advancement = top-N per group)
//   • Phase separation (knockout cannot start before the group stage in the class is finished)
//   • Rest time between semi and final (minRestMinutes)
//   • Showcase windows (reserved times for finals)
//   • Breaks in the middle of the day
//
// Takes pairCountsByBucket = { bucketKey: pairCount }, and returns:
//   { ok: bool, perBucket: { bucketKey: {needMin, capacityMin, deficit, ...} },
//     issues: [ {bucketKey, label, severity, msg} ], totalNeedMin, totalCapacityMin }
export function simulateKMCapacity({
  days = [],
  buckets = [],
  pairCountsByBucket = {},
  defaultCourts = 4,
  bufferMinutes = 0,
  groupSize = 4,
  advancingPerGroup = 2,
  groupMatchMinutes = 60,
  knockoutMatchMinutes = 75,
  minRestMinutes = 30,
  pacingMode = 'interleaved',
  showcaseConfig = null,
  // Per-bucket knockout config: { [bucketKey]: { format, bronzeFinal, bBronzeFinal, hasBBracket } }
  // format: 'auto' | 'semifinals' | 'quarters' | 'r16' | 'r32' | 'finalOnly'
  //   - 'finalOnly': only 1 final (no semi/quarter) — for small classes or
  //     when admin wants to skip knockout entirely
  //   - 'semifinals': 4 pairs, 2 semis + final (default)
  //   - 'quarters': 8 pairs, 4 quarters + 2 semis + final
  // bronzeFinal / bBronzeFinal: true means a bronze final in A / B — the same keys
  //   and the same «off unless true» reading as the generator (#882). The key
  //   used to be `hasBronze`, read as default ON, so every estimate counted a
  //   bronze final the generator does not build.
  // hasBBracket: true adds a parallel knockout for the losers (usually half size)
  knockoutByBucket = {},
}) {
  const buf = bufferMinutes || 0;
  const gm = groupMatchMinutes;
  const km = knockoutMatchMinutes;
  const rest = minRestMinutes || 0;

  // 1) Total court time per day (minutes of court capacity = day minutes × courts)
  // and total capacity across all days.
  let totalCapacityMin = 0;
  let lastNonReservedDayCourts = defaultCourts;
  let lastNonReservedDayMin = 0;
  days.forEach((d) => {
    if (!d.startTime || !d.endTime) return;
    if (d.reserved) return;
    const [h1, m1] = d.startTime.split(':').map(Number);
    const [h2, m2] = d.endTime.split(':').map(Number);
    const dayMin = Math.max(0, h2 * 60 + m2 - (h1 * 60 + m1));
    const breakMin = (d.breaks || []).reduce((s, b) => s + (b.durationMin || 0), 0);
    const playable = Math.max(0, dayMin - breakMin);
    const courts = d.courts || defaultCourts || 0;
    totalCapacityMin += playable * courts;
    lastNonReservedDayCourts = courts;
    lastNonReservedDayMin = playable;
  });

  // 2) Subtract showcase windows that reserve courts in specific time ranges.
  // (Each showcase block reserves 1+ courts in a time window.)
  let showcaseReservedMin = 0;
  if (showcaseConfig && Array.isArray(showcaseConfig.windows)) {
    showcaseConfig.windows.forEach((w) => {
      if (!w.start || !w.end || !w.courts) return;
      const [h1, m1] = w.start.split(':').map(Number);
      const [h2, m2] = w.end.split(':').map(Number);
      const winMin = Math.max(0, h2 * 60 + m2 - (h1 * 60 + m1));
      showcaseReservedMin += winMin * w.courts;
    });
  }
  const availableMin = Math.max(0, totalCapacityMin - showcaseReservedMin);

  // 3) For each bucket: compute actual match usage based on the number of pairs.
  //    Round-robin: C(groupSize, 2) matches per group × ceil(N/groupSize) groups.
  //    Knockout bracket: 2^ceil(log2(advanceCount)) — minus 1 (single elim) +
  //    1 bronze final + 1 (possibly) extra depending on the bracket.
  const perBucket: any = {};
  let totalNeedMin = 0;
  buckets.forEach((b) => {
    const N = pairCountsByBucket[b.bucketKey] || 0;
    if (N < 2) {
      perBucket[b.bucketKey] = {
        N,
        groupMatches: 0,
        knockoutMatches: 0,
        groupNeedMin: 0,
        knockoutNeedMin: 0,
        needMin: 0,
        label: b.label,
      };
      return;
    }
    const groups = Math.ceil(N / groupSize);
    const matchesPerGroup = Math.max(0, (groupSize * (groupSize - 1)) / 2);
    // For uneven groups the last group gets fewer matches. Practical approximation:
    // keep matchesPerGroup but adjust down if the last group is smaller (rarely more
    // than 1 pair off).
    const last = N - (groups - 1) * groupSize;
    let groupMatches;
    if (last >= 2 && last < groupSize) {
      const lastMatches = (last * (last - 1)) / 2;
      groupMatches = (groups - 1) * matchesPerGroup + lastMatches;
    } else if (last < 2) {
      groupMatches = (groups - 1) * matchesPerGroup;
    } else {
      groupMatches = groups * matchesPerGroup;
    }
    // Knockout: every pair that advances ends up in the bracket. The bracket size
    // is determined by the knockout format — admin can force a smaller or larger
    // bracket per class, and decide whether the bronze final + B-knockout should
    // be included. We compute the actual matches based on that.
    const koCfg = knockoutByBucket[b.bucketKey] || {};
    const koFormat = koCfg.format || 'auto';
    const includeBronze = kmBronzeFinalOn(koCfg); // default OFF, as the generator (#882)
    const includeBBronze = koCfg.bBronzeFinal === true; // B's own bronze, as the generator
    const includeBBracket = koCfg.hasBBracket === true; // default false
    let advanceCount;
    let brackSize;
    if (koFormat === 'finalOnly') {
      // Only 1 final (gold) — admin has chosen to skip the knockout
      advanceCount = 2;
      brackSize = 2;
    } else if (koFormat === 'semifinals') {
      advanceCount = Math.min(N, 4);
      brackSize = 4;
    } else if (koFormat === 'quarters') {
      advanceCount = Math.min(N, 8);
      brackSize = 8;
    } else if (koFormat === 'r16') {
      advanceCount = Math.min(N, 16);
      brackSize = 16;
    } else if (koFormat === 'r32') {
      advanceCount = Math.min(N, 32);
      brackSize = 32;
    } else {
      // 'auto': based on the number of groups and top-N
      advanceCount = Math.min(N, groups * advancingPerGroup);
      brackSize = advanceCount >= 2 ? Math.pow(2, Math.ceil(Math.log2(advanceCount))) : 0;
    }
    let knockoutMatches = 0;
    if (advanceCount >= 2 && brackSize >= 2) {
      // Single-elim: bracketSize - 1 matches
      // Bronze final: only if admin chose it, and at least 4 pairs (= semifinals)
      knockoutMatches = kmKnockoutMatchCount(brackSize, includeBronze && advanceCount >= 4);
      // B-knockout: parallel bracket for the first-round losers. We model
      // it as roughly half the A-bracket (the quarter losers become B-semis etc.)
      if (includeBBracket && brackSize >= 4) {
        const bSize = brackSize / 2;
        knockoutMatches += bSize - 1; // single-elim in B
        if (includeBBronze && bSize >= 4) knockoutMatches += 1;
      }
    }
    const groupNeedMin = groupMatches * (gm + buf);
    const knockoutNeedMin = knockoutMatches * (km + buf);
    // Rest time between semi and final: if there are at least 4 pairs in the
    // knockout (= semifinal + final), add minRestMinutes as dead time on 1 court.
    // Rarely more than one such blockade per bucket — we approximate.
    const restPenalty = advanceCount >= 4 ? rest : 0;
    const needMin = groupNeedMin + knockoutNeedMin + restPenalty;
    perBucket[b.bucketKey] = {
      N,
      groups,
      groupMatches,
      knockoutMatches,
      advanceCount,
      groupNeedMin,
      knockoutNeedMin,
      restPenalty,
      needMin,
      label: b.label,
      koFormat,
      includeBronze,
      includeBBracket,
      brackSize,
    };
    totalNeedMin += needMin;
  });

  // 4) Phase-separation cost: not all courts are useful all the time. When one
  //    class goes from group to knockout, the first knockout matches are often limited
  //    by the bracket size (4 semifinals = needs 2 courts). If we have more courts
  //    than that, some go idle for a moment. Approximation: add a 5% "pacing
  //    loss" for interleaved (classes cross each other nicely), 10% for prioritized
  //    (classes are split, so idle time between them), 15% for serial (one class at a time).
  const pacingLoss = pacingMode === 'serial' ? 0.15 : pacingMode === 'prioritized' ? 0.1 : 0.05;
  const effectiveCapacityMin = Math.floor(availableMin * (1 - pacingLoss));

  // 5) Generate issues
  const issues = [];
  const overall = totalNeedMin > effectiveCapacityMin;
  if (overall) {
    const deficit = totalNeedMin - effectiveCapacityMin;
    issues.push({
      severity: 'error',
      msg: `Total banetid for kort med ca. ${Math.round(deficit / 60)} timer. Trenger ~${Math.round(totalNeedMin / 60)}t, har ~${Math.round(effectiveCapacityMin / 60)}t.`,
    });
  }
  // Specifically: a large knockout that does not fit in one day on the last day
  // (knockout is typically scheduled last). If the last day has too little left for
  // the knockout, warn.
  const knockoutNeedSum: number = (Object.values(perBucket) as any[]).reduce(
    (s: number, x: any) => s + (x.knockoutNeedMin || 0) + (x.restPenalty || 0),
    0,
  );
  const lastDayCapacity = lastNonReservedDayMin * lastNonReservedDayCourts;
  if (knockoutNeedSum > lastDayCapacity * 0.7) {
    issues.push({
      severity: 'warning',
      msg: `Sluttspillet trenger ~${Math.round(knockoutNeedSum / 60)}t men siste dag har bare ~${Math.round(lastDayCapacity / 60)}t. Vurder å reservere mer tid til finalene.`,
    });
  }

  // 6) Naive max estimate for backward compatibility (same formula as before)
  const timePerPair = 1.5 * (gm + buf) + 0.5 * (km + buf);
  const maxPairs = timePerPair > 0 ? Math.floor(totalCapacityMin / timePerPair) : 0;

  return {
    ok: !overall,
    totalCapacityMin,
    availableMin,
    effectiveCapacityMin,
    showcaseReservedMin,
    pacingLoss,
    totalNeedMin,
    perBucket,
    issues,
    // Backward-compat fields
    courtMinutes: totalCapacityMin,
    timePerPair,
    maxPairs,
  };
}

// ---------- 7) Pair building ----------
// pair.elo = sum of both players' ELO by default (admin can override via seedOverride)
export function buildPairsFromPlayers(playerPairs, allPlayers) {
  return (playerPairs || []).map((pp, idx) => {
    const p1 = allPlayers.find((x) => x.id === pp.p1Id);
    const p2 = allPlayers.find((x) => x.id === pp.p2Id);
    const elo1 = p1?.elo || 1500;
    const elo2 = p2?.elo || 1500;
    return {
      id:
        pp.id ||
        `pair_${pp.divisionId || 'd'}_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 8)}`,
      p1Id: pp.p1Id,
      p2Id: pp.p2Id,
      p1Name: p1?.name || '?',
      p2Name: p2?.name || '?',
      eloSum: elo1 + elo2,
      eloAvg: (elo1 + elo2) / 2,
      seedOverride: pp.seedOverride ?? null, // if admin has drag-dropped
      classId: pp.classId,
      divisionId: pp.divisionId,
    };
  });
}

// Sorts pairs by (manual override OR eloSum desc)
export function sortPairsBySeed(pairs) {
  return [...pairs].sort((a, b) => {
    const aOv = a.seedOverride ?? null;
    const bOv = b.seedOverride ?? null;
    if (aOv != null && bOv != null) return aOv - bOv;
    if (aOv != null) return -1;
    if (bOv != null) return 1;
    return (b.eloSum || 0) - (a.eloSum || 0);
  });
}

// ---------- 8) Build group-stage matches for one division ----------
export function buildDivisionGroupMatches(division, groups) {
  const matches = [];
  groups.forEach((group, _gi) => {
    const rounds = roundRobinSchedule(group.pairs);
    rounds.forEach((round, ri) => {
      round.forEach((m, mi) => {
        matches.push({
          id: `km_${division.id}_${group.id}_r${ri + 1}_m${mi + 1}`,
          phase: 'group',
          classId: division.classId,
          divisionId: division.id,
          groupId: group.id,
          groupName: group.name,
          roundIndex: ri + 1,
          team1: { pairId: m.p1.id, p1Id: m.p1.p1Id, p2Id: m.p1.p2Id },
          team2: { pairId: m.p2.id, p1Id: m.p2.p1Id, p2Id: m.p2.p2Id },
          court: null,
          startTime: null,
          endTime: null,
          score: null,
          playerEloPre: null,
          matchFormat: division.groupMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
          minutesPerMatch: division.groupMinutes || KM_DEFAULT_GROUP_MINUTES,
        });
      });
    });
  });
  return matches;
}

// Default format for a SMALL class (fewer than KM_MIN_PAIRS_FOR_GROUP_STAGE
// pairs = one group): league
// play — everyone meets everyone and the table decides («seriespill»). A
// knockout on top (final / semi-finals) and the pure cup are explicit choices.
export function defaultSmallClassFormat(_pairCount: number): 'semifinals' | 'final' | 'group' {
  return 'group';
}

// ---------- 9) Build knockout (placeholder matches) ----------
export function buildDivisionKnockoutMatches(division, groups) {
  if (!division.useGroupStage) {
    // Pure cup: use FIP-bracket
    try {
      const sortedPairs = sortPairsBySeed(groups.flatMap((g) => g.pairs));
      const slotted = fipBracketSeed(sortedPairs);
      const out = [];
      const n = slotted.length;
      const roundName = n === 4 ? 'sf' : n === 8 ? 'qf' : n === 16 ? 'r16' : 'r32';
      for (let i = 0; i < n / 2; i++) {
        const p1 = slotted[i * 2];
        const p2 = slotted[i * 2 + 1];
        // One side empty = a BYE: the present pair walks over into the next round.
        // Reuse the existing walkover mechanism (score.walkover) — it already reads
        // as WO in the UI and is kept out of ELO — and mark it `bye` so it can be
        // told apart from a real no-show. A bye needs no court and no playing time.
        const isBye = (!p1 && !!p2) || (!!p1 && !p2);
        const byeWinner = isBye ? (p1 ? 't1' : 't2') : null;
        out.push({
          id: `km_${division.id}_ko_${roundName}_s${i + 1}`,
          phase: 'knockout',
          round: roundName,
          slot: i + 1,
          classId: division.classId,
          divisionId: division.id,
          bracket: 'A',
          team1: { pairId: p1?.id, p1Id: p1?.p1Id, p2Id: p1?.p2Id },
          team2: { pairId: p2?.id, p1Id: p2?.p1Id, p2Id: p2?.p2Id },
          court: null,
          startTime: null,
          endTime: null,
          bye: isBye,
          score: isBye
            ? {
                t1: byeWinner === 't1' ? 1 : 0,
                t2: byeWinner === 't2' ? 1 : 0,
                sets: [],
                walkover: true,
                woWinner: byeWinner,
                bye: true,
              }
            : null,
          playerEloPre: null,
          matchFormat: division.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
          minutesPerMatch: isBye ? 0 : division.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES,
        });
      }
      // The later rounds (SF, F, optional bronze) — same builder as the group
      // path. Without this a pure-cup division (< 8 pairs, no group stage)
      // stopped after its first round: a 5-pair class got one real match plus
      // three byes and never a semi-final or final.
      return [...out, ...buildSubsequentARounds(out, division)];
    } catch (e) {
      console.warn(`[KM] FIP-bracket bygging feilet for divisjon ${division.id}: ${errMsg(e)}`);
      return [];
    }
  }
  // With a group stage: cross-over (or cross-over with lucky losers)
  try {
    const bracket = buildCrossOverBracket(groups, division.advanceTopN || 2, {
      luckyLosers: division.luckyLosers || 0,
      bracketSize: division.bracketSize || 0,
    });
    // p1/p2 can be either { from: 'group', ... } or { from: 'lucky_loser', llRank, seed }
    const buildTeamPlaceholder = (p) => {
      if (!p) return { from: 'tbd', pairId: null };
      if (p.from === 'lucky_loser') {
        return { from: 'lucky_loser', llRank: p.llRank, seed: p.seed, pairId: null };
      }
      return {
        from: 'group',
        groupId: p.groupId,
        groupName: p.groupName,
        pos: p.pos,
        pairId: null,
      };
    };
    const aMatches = bracket.map((m, _idx) => ({
      id: `km_${division.id}_ko_${m.round}_s${m.slot}`,
      phase: 'knockout',
      round: m.round,
      slot: m.slot,
      classId: division.classId,
      divisionId: division.id,
      bracket: 'A',
      team1: buildTeamPlaceholder(m.p1),
      team2: buildTeamPlaceholder(m.p2),
      court: null,
      startTime: null,
      endTime: null,
      score: null,
      playerEloPre: null,
      matchFormat: division.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
      minutesPerMatch: division.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES,
    }));

    // Generate the subsequent A-bracket rounds (SF, F, bronze) as placeholders
    const subsequentA = buildSubsequentARounds(aMatches, division);
    const fullAMatches = [...aMatches, ...subsequentA];

    // B-knockout: the teams that did NOT advance to A (the bottom positions in the
    // groups) play a separate knockout that mirrors A. Controlled by division.consolationBracket.
    if (division.consolationBracket && aMatches.length >= 2) {
      const bMatches = buildBBracketFromGroups(groups, division);
      return [...fullAMatches, ...bMatches];
    }

    return fullAMatches;
  } catch (e) {
    console.warn(`[KM] Cross-over bygging feilet for divisjon ${division.id}: ${errMsg(e)}`);
    return [];
  }
}

// Build the subsequent A-bracket rounds (SF, F, bronze) from the first round
// After QF: 2 SF + 1 F + 1 Bronze
// After R16: 4 QF + 2 SF + 1 F + 1 Bronze
// After SF: 1 F + 1 Bronze
export function buildSubsequentARounds(firstRoundMatches, division) {
  if (!firstRoundMatches || firstRoundMatches.length < 2) return [];

  const roundOrder = ['r32', 'r16', 'qf', 'sf', 'f'];
  const firstRoundName = firstRoundMatches[0].round;
  const firstIdx = roundOrder.indexOf(firstRoundName);
  if (firstIdx < 0 || firstIdx >= roundOrder.length - 1) return [];

  const output = [];
  let prevRoundName = firstRoundName;
  let prevSize = firstRoundMatches.length;

  // Build the subsequent rounds up to the final
  for (let i = firstIdx + 1; i < roundOrder.length; i++) {
    const thisRound = roundOrder[i];
    const thisSize = prevSize / 2;
    if (thisSize < 1) break;

    for (let s = 0; s < thisSize; s++) {
      output.push({
        id: `km_${division.id}_ko_${thisRound}_s${s + 1}`,
        phase: 'knockout',
        round: thisRound,
        slot: s + 1,
        classId: division.classId,
        divisionId: division.id,
        bracket: 'A',
        team1: { from: 'a_winner', prevRound: prevRoundName, prevSlot: s * 2 + 1, pairId: null },
        team2: { from: 'a_winner', prevRound: prevRoundName, prevSlot: s * 2 + 2, pairId: null },
        court: null,
        startTime: null,
        endTime: null,
        score: null,
        playerEloPre: null,
        matchFormat: division.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
        minutesPerMatch: division.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES,
      });
    }

    prevRoundName = thisRound;
    prevSize = thisSize;
  }

  // Bronze final: the SF losers play for 3rd place.
  //
  // OPT-IN, NOT DEFAULT-ON — CONFIRMED BY THE CTO (#882, 2026-10-18). An
  // undefined value means NO bronze final. `createKlubbmesterskapTournament`
  // never sets the key, and the knockout panel reads `div.bronzeFinal === true`,
  // so a fresh tournament has no bronze final unless somebody ticks the box.
  // (Until #496 the comment here claimed «default ON when undefined»; the code
  // never did.) `kmBronzeFinalOn` is the same predicate the match COUNTERS use.
  // Pinned by «CHARACTERIZED: no bronze final unless the division explicitly
  // asks for one».
  // IMPORTANT: the SF matches can be either the FIRST round (4-team bracket → passed in
  // as firstRoundMatches) or a later round (8+ teams → built in output).
  // So check BOTH sources, otherwise bronze is missing in 4-team brackets.
  const sfMatches = [...firstRoundMatches, ...output].filter((m) => m.round === 'sf');
  if (sfMatches.length === 2 && kmBronzeFinalOn(division)) {
    output.push({
      id: `km_${division.id}_ko_bronze_s1`,
      phase: 'knockout',
      round: 'bronze',
      slot: 1,
      classId: division.classId,
      divisionId: division.id,
      bracket: 'A',
      team1: { from: 'a_loser', prevRound: 'sf', prevSlot: 1, pairId: null },
      team2: { from: 'a_loser', prevRound: 'sf', prevSlot: 2, pairId: null },
      court: null,
      startTime: null,
      endTime: null,
      score: null,
      playerEloPre: null,
      matchFormat: division.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
      minutesPerMatch: division.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES,
    });
  }

  return output;
}

// Build the B-knockout from the BOTTOM teams in the groups (those that did not advance to A).
// Mirrors the A-bracket's cross-over pattern, but the placeholders point to
// the positions JUST BELOW the A-qualification (pos advanceTopN+1, +2, …).
// Example: 4 groups of 4 pairs, top 2 to A → bottom 2 (pos 3-4) to B.
export function buildBBracketFromGroups(groups, division) {
  const advanceTopN = division.advanceTopN || 2; // how many advanced to A
  const bAdvanceTopN = division.bAdvanceTopN || advanceTopN; // how many to B per group (own format)
  // B takes the teams JUST BELOW the A-qualification. Use B's own advance count
  // in the cross-over pattern, but shift the positions past A's slots.
  let pattern;
  try {
    pattern = buildCrossOverBracket(groups, bAdvanceTopN, {});
  } catch (_e) {
    return [];
  }
  if (!pattern || pattern.length < 1) return [];

  const offset = advanceTopN; // B-pos starts at advanceTopN+1
  const bRound = (r) => `b_${r}`;
  const fmt = division.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT;
  const mins = division.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES;

  // Shift positions: pos N (top) → pos N+offset (bottom), only for group slots.
  const shiftPos = (p) => {
    if (!p) return { from: 'tbd', pairId: null };
    if (p.from === 'group') {
      return {
        from: 'group',
        groupId: p.groupId,
        groupName: p.groupName,
        pos: p.pos + offset,
        pairId: null,
      };
    }
    // We don't put lucky losers in B (rarely meaningful) — fall through to TBD
    return { from: 'tbd', pairId: null };
  };

  const out = [];
  // First B-round (mirrors A's first round)
  pattern.forEach((m) => {
    out.push({
      id: `km_${division.id}_kb_${bRound(m.round)}_s${m.slot}`,
      phase: 'knockout',
      round: bRound(m.round),
      slot: m.slot,
      classId: division.classId,
      divisionId: division.id,
      bracket: 'B',
      team1: shiftPos(m.p1),
      team2: shiftPos(m.p2),
      court: null,
      startTime: null,
      endTime: null,
      score: null,
      playerEloPre: null,
      matchFormat: fmt,
      minutesPerMatch: mins,
    });
  });

  // Subsequent B-rounds (winners meet) up to the B-final + optional B-bronze
  const roundOrder = ['r32', 'r16', 'qf', 'sf', 'f'];
  const firstRound = pattern[0].round;
  const firstIdx = roundOrder.indexOf(firstRound);
  let prevRoundName = bRound(firstRound);
  let prevSize = pattern.length;
  for (let i = firstIdx + 1; i < roundOrder.length; i++) {
    const thisSize = prevSize / 2;
    if (thisSize < 1) break;
    const thisRound = bRound(roundOrder[i]);
    for (let s = 0; s < thisSize; s++) {
      out.push({
        id: `km_${division.id}_kb_${thisRound}_s${s + 1}`,
        phase: 'knockout',
        round: thisRound,
        slot: s + 1,
        classId: division.classId,
        divisionId: division.id,
        bracket: 'B',
        team1: { from: 'b_winner', bRound: prevRoundName, bSlot: s * 2 + 1, pairId: null },
        team2: { from: 'b_winner', bRound: prevRoundName, bSlot: s * 2 + 2, pairId: null },
        court: null,
        startTime: null,
        endTime: null,
        score: null,
        playerEloPre: null,
        matchFormat: fmt,
        minutesPerMatch: mins,
      });
    }
    prevRoundName = thisRound;
    prevSize = thisSize;
  }

  // B-bronze final (the losers of the B-semifinals) — separate setting (independent of A)
  const bSf = out.filter((m) => m.round === 'b_sf');
  if (bSf.length === 2 && division.bBronzeFinal === true) {
    out.push({
      id: `km_${division.id}_kb_bronze_s1`,
      phase: 'knockout',
      round: 'b_bronze',
      slot: 1,
      classId: division.classId,
      divisionId: division.id,
      bracket: 'B',
      team1: { from: 'b_loser', bRound: 'b_sf', bSlot: 1, pairId: null },
      team2: { from: 'b_loser', bRound: 'b_sf', bSlot: 2, pairId: null },
      court: null,
      startTime: null,
      endTime: null,
      score: null,
      playerEloPre: null,
      matchFormat: fmt,
      minutesPerMatch: mins,
    });
  }

  return out;
}

// Build B-bracket matches. The losers of the A-bracket rounds meet in the B-bracket.
// Strategy:
//   - For each first A-round (QF or R16): pair up the losers in the B-first-round
//   - The winners of the B-first-round advance to the B-semifinals/final
export function buildConsolationMatches(aMatches, division) {
  // Find the first round in the A-bracket
  const roundOrder = { r32: 1, r16: 2, qf: 3, sf: 4, f: 5 };
  const firstRound = aMatches.reduce((min, m) => {
    const r = roundOrder[m.round];
    return r != null && (min == null || r < min.order) ? { round: m.round, order: r } : min;
  }, null);

  if (!firstRound) return [];

  // Losers from the first A-round (sorted by slot)
  const firstRoundMatches = aMatches
    .filter((m) => m.round === firstRound.round)
    .sort((a, b) => (a.slot || 0) - (b.slot || 0));

  if (firstRoundMatches.length < 2) return [];

  // B-bracket size = number of losers (same as A-bracket size / 2)
  const bSize = firstRoundMatches.length;
  const bMatches = [];

  // B-round 1: pair up the losers
  // loser-of-slot-1 vs loser-of-slot-2, loser-of-slot-3 vs loser-of-slot-4, etc.
  // Round name for B: prefix 'b_'
  const bRoundName = (n) => {
    if (n === 2) return 'b_f';
    if (n === 4) return 'b_sf';
    if (n === 8) return 'b_qf';
    return 'b_r' + n;
  };

  // Build the rounds through recursion: bRound1 → bRound2 → ... → bFinal
  const _currentSize = bSize;
  const prevRoundName = firstRound.round; // the A-bracket round that supplies the losers

  // First B-round: losers of the A-bracket's first round
  const round1Name = bRoundName(bSize);
  for (let i = 0; i < bSize / 2; i++) {
    bMatches.push({
      id: `km_${division.id}_kb_${round1Name}_s${i + 1}`,
      phase: 'knockout',
      round: round1Name,
      slot: i + 1,
      classId: division.classId,
      divisionId: division.id,
      bracket: 'B',
      team1: { from: 'a_loser', aRound: prevRoundName, aSlot: i * 2 + 1, pairId: null },
      team2: { from: 'a_loser', aRound: prevRoundName, aSlot: i * 2 + 2, pairId: null },
      court: null,
      startTime: null,
      endTime: null,
      score: null,
      playerEloPre: null,
      matchFormat: division.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
      minutesPerMatch: division.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES,
    });
  }

  // If bSize > 2, build more rounds up to the B-final
  let prevBRoundName = round1Name;
  let prevBSize = bSize;
  while (prevBSize > 2) {
    const nextSize = prevBSize / 2;
    const thisRoundName = bRoundName(nextSize);
    for (let i = 0; i < nextSize / 2; i++) {
      bMatches.push({
        id: `km_${division.id}_kb_${thisRoundName}_s${i + 1}`,
        phase: 'knockout',
        round: thisRoundName,
        slot: i + 1,
        classId: division.classId,
        divisionId: division.id,
        bracket: 'B',
        team1: { from: 'b_winner', bRound: prevBRoundName, bSlot: i * 2 + 1, pairId: null },
        team2: { from: 'b_winner', bRound: prevBRoundName, bSlot: i * 2 + 2, pairId: null },
        court: null,
        startTime: null,
        endTime: null,
        score: null,
        playerEloPre: null,
        matchFormat: division.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
        minutesPerMatch: division.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES,
      });
    }
    prevBRoundName = thisRoundName;
    prevBSize = nextSize;
  }

  return bMatches;
}

// ---------- 10) Main factory: createKlubbmesterskapTournament ----------
export function createKlubbmesterskapTournament({
  name = 'Klubbmesterskap',
  classes, // [{ id, name, gender, divisions: [{...}] }]
  courts = 4,
  days = [], // [{ date, startTime, endTime, courts, matchDuration, breaks }]
  bufferMinutes = KM_DEFAULT_BUFFER_MINUTES,
  minRestMinutes = 30, // minimum rest time between matches for the same pair
  avoidBackToBack = true, // soft preference: avoid pairs playing right after one another
  pacingMode = 'interleaved', // 'interleaved' | 'serial' | 'prioritized'
  classPriority = [], // bucket-keys in priority order (for 'prioritized')
  courtPriority = null, // [{ id, enabled }] — rules for court prioritization
  courtRank = null, // [1, 2, 3, ...] — courts in order from most to least attractive
  forceCourtPriority = false, // allow an empty court to get the top match on the best available court
  rankedCourtCount = null, // number of top courts that are ranked; the rest are distributed randomly (null = all)
  showcaseConfig = null, // { men_A: {...}, women_A: {...} } — fixed windows for A-class semis/finals
  kMultiplier = 1.75, // ELO weight (1.0 = 100%). KM-standard 175% — a mix of super-tiebreak and a full 3rd set.
  allPlayers = [],
}) {
  if (!Array.isArray(classes) || classes.length === 0) {
    throw new Error('createKlubbmesterskapTournament krever minst én klasse');
  }

  const tournament = {
    id: `km_${Date.now()}`,
    type: 'klubbmesterskap',
    name,
    config: {
      tournamentType: 'klubbmesterskap', // consistent with the Padel24/Americano pattern
      name, // so pause/archive lists show the tournament name
      classes: [],
      courts,
      days,
      bufferMinutes,
      minRestMinutes,
      avoidBackToBack,
      pacingMode,
      classPriority,
      courtPriority,
      courtRank,
      forceCourtPriority,
      rankedCourtCount,
      showcaseConfig,
      kMultiplier,
    },
    pairs: [],
    groups: [], // all groups across classes/divisions
    matches: [], // all matches across them
    status: 'setup',
    createdAt: new Date().toISOString(),
  };

  // Build each class → each division → pairs → groups → matches
  classes.forEach((cls) => {
    const classOut = {
      id: cls.id,
      name: cls.name,
      gender: cls.gender,
      divisions: [],
    };

    (cls.divisions || []).forEach((div) => {
      const builtPairs = buildPairsFromPlayers(
        (div.pairs || []).map((p) => ({ ...p, classId: cls.id, divisionId: div.id })),
        allPlayers,
      );
      const sortedPairs = sortPairsBySeed(builtPairs);

      const useGroupStage = div.useGroupStage !== false; // default on

      // Auto-determine optimal group sizes based on the number of pairs
      // (preferably 4 per group, max 5). Can be overridden by explicitly passing groupSizes,
      // or derived from groupAssignments (manual wizard override).
      const hasGroupAssignments =
        useGroupStage && Array.isArray(div.groupAssignments) && div.groupAssignments.length > 0;
      const groupSizes = useGroupStage
        ? div.groupSizes && Array.isArray(div.groupSizes)
          ? div.groupSizes
          : hasGroupAssignments
            ? div.groupAssignments.map((arr) => arr.length) // derive from the manual override
            : determineGroupSizes(sortedPairs.length)
        : [sortedPairs.length];
      const groupCount = groupSizes.length;

      // Auto-determine the knockout structure based on the number of groups, but let admin
      // override the format per class (div.knockoutOverride: 'semifinals'|'quarters'|'r16').
      const bracketStructure = useGroupStage
        ? determineBracketStructure(groupCount, sortedPairs.length, div.knockoutOverride || 'auto')
        : {
            advanceTopN: div.advanceTopN || 2,
            bracketSize: 0,
            luckyLosers: 0,
            format: 'cup_only',
            formatLabel: 'Direkte cup (ingen gruppespill)',
          };

      // Build the groups. If div.groupAssignments is given (manual override from the
      // wizard's Groups step), use it. Format: [[pairId, pairId, ...], [...], ...]
      // — one array per group (A, B, C, ...). Otherwise default to snake seeding.
      let groups;
      if (hasGroupAssignments) {
        const pairById = new Map(builtPairs.map((p) => [p.id, p]));
        groups = div.groupAssignments.map((pairIds, i) => {
          const groupId = `grp_${String.fromCharCode(65 + i).toLowerCase()}`;
          const groupPairs = pairIds
            .map((pid) => pairById.get(pid))
            .filter(Boolean)
            .map((p, seedIdx) => ({
              ...p,
              seed: p.seed || i * 100 + seedIdx + 1,
              groupId, // pair objects inside groups must know their own groupId
              classId: cls.id,
              divisionId: div.id,
            }));
          return {
            id: groupId,
            name: String.fromCharCode(65 + i),
            pairs: groupPairs,
            capacity: groupSizes[i] || groupPairs.length,
          };
        });
      } else if (useGroupStage) {
        groups = snakeSeedGroups(sortedPairs, groupSizes);
        // Here too: set groupId/classId/divisionId on each pair in each group
        groups = groups.map((g) => ({
          ...g,
          pairs: g.pairs.map((p) => ({ ...p, groupId: g.id, classId: cls.id, divisionId: div.id })),
        }));
      } else {
        const soloGroupId = `solo_${div.id}`;
        groups = [
          {
            id: soloGroupId,
            name: 'Cup',
            pairs: sortedPairs.map((p, i) => ({
              ...p,
              seed: i + 1,
              groupId: soloGroupId,
              classId: cls.id,
              divisionId: div.id,
            })),
          },
        ];
      }

      const divOut = {
        id: div.id,
        name: div.name,
        classId: cls.id,
        pairCount: sortedPairs.length,
        groupCount: useGroupStage ? groupCount : 0,
        groupSizes,
        advanceTopN: bracketStructure.advanceTopN,
        luckyLosers: bracketStructure.luckyLosers,
        bracketSize: bracketStructure.bracketSize,
        bracketFormat: bracketStructure.format,
        bracketFormatLabel: bracketStructure.formatLabel,
        useGroupStage,
        consolationBracket: div.consolationBracket === true,
        groupMatchFormat: div.groupMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
        knockoutMatchFormat: div.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
        groupMinutes: div.groupMinutes || KM_DEFAULT_GROUP_MINUTES,
        knockoutMinutes: div.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES,
        groups: groups.map((g) => ({ id: g.id, name: g.name, pairIds: g.pairs.map((p) => p.id) })),
      };

      // Set groupId on the pairs when pushing (so tournament.pairs reflects the
      // group distribution we have now decided — whether it is the snake default or
      // a manual override from the wizard)
      const groupIdByPairId = new Map();
      groups.forEach((g) => {
        g.pairs.forEach((p) => groupIdByPairId.set(p.id, g.id));
      });
      tournament.pairs.push(
        ...sortedPairs.map((p, i) => ({
          ...p,
          seed: i + 1,
          groupId: groupIdByPairId.get(p.id) || null,
        })),
      );
      tournament.groups.push(
        ...groups.map((g) => ({
          ...g,
          classId: cls.id,
          divisionId: div.id,
        })),
      );

      // Group-stage matches
      if (useGroupStage) {
        tournament.matches.push(
          ...buildDivisionGroupMatches({ ...divOut, classId: cls.id }, groups),
        );
      }

      // Knockout matches (placeholders)
      tournament.matches.push(
        ...buildDivisionKnockoutMatches({ ...divOut, classId: cls.id }, groups),
      );

      classOut.divisions.push(divOut);
    });

    tournament.config.classes.push(classOut);
  });

  // Place all matches on actual time slots
  scheduleKMMatches(tournament);

  // After scheduling: mark as ready to start. Admin can still adjust
  // groups/pairs/courts etc., but it gets locked when 'Start tournament' is pressed.
  tournament.status = 'ready';

  return tournament;
}

// ---------- Helper: format minutes (since midnight) as HH:MM ----------
export function formatMinutesAsTime(min) {
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ---------- 10b) Scheduling: place matches on actual times and courts ----------
// Strategy:
//   1. Sort matches into the desired play order:
//        - Group stage first: round by round (R1 in all classes/groups, then R2, ...)
//        - Knockout after the group stage: R32 → R16 → QF → SF → F + bronze
//   2. Build a time-slot stream per court per day
//   3. Place each match in the next free slot, accounting for match duration and buffer
//   4. Ensure "pacing": the same pair should preferably not play two matches right after one another
//      (we try to push to the next available slot where the pair has not just played)
export function scheduleKMMatches(tournament) {
  const config = tournament.config || {};
  const days = config.days || [];
  const courts = config.courts || 4;
  const bufferMin = config.bufferMinutes ?? 5;
  const minRestMin = config.minRestMinutes || 30; // minimum rest time between matches for the same pair
  // Soft preference: try to avoid the same pair playing right after one another.
  // Default on. Only affects which free slot is chosen, never validity.
  const avoidBackToBack = config.avoidBackToBack !== false;
  const pacingMode = config.pacingMode || 'interleaved';
  const classPriority = Array.isArray(config.classPriority) ? config.classPriority : [];

  if (days.length === 0 || tournament.matches.length === 0) return tournament;

  // Build a bucketKey for each match based on class + division. Corresponds to
  // the wizard's bucketKey ('men_A', 'women_B', etc.).
  // We use the class+division name to match the user's classPriority array.
  const bucketKeyForMatch = (m) => {
    // Look up the class and division in config
    const cls = (config.classes || []).find((c) => c.id === m.classId);
    if (!cls) return m.classId || '?';
    const gender = cls.gender || '';
    const div = (cls.divisions || []).find((d) => d.id === m.divisionId);
    const divName = div?.name || '?';
    // Map gender to the bucket prefix
    const catId = gender === 'M' ? 'men' : gender === 'F' ? 'women' : 'mix';
    return `${catId}_${divName}`;
  };

  // Pre-compute classKey for each match (to use in sorting)
  tournament.matches.forEach((m) => {
    m._classKey = bucketKeyForMatch(m);
  });

  // Class-ranking helper: returns the GROUP index in the priority list
  // (lower = earlier, same number = parallel).
  //
  // Backward compat: classPriority can be either
  //   (a) a flat array of bucket-keys (old format) — one bucket per group, or
  //   (b) an array of arrays (new format) — several bucket-keys in the same group play in parallel.
  // Classes not in the list end up last (alphabetically via a separate sort).
  const isNestedPriority =
    Array.isArray(classPriority) && classPriority.length > 0 && Array.isArray(classPriority[0]);
  const classGroupIdx = (key) => {
    if (!key || !Array.isArray(classPriority) || classPriority.length === 0) return 999;
    if (isNestedPriority) {
      for (let i = 0; i < classPriority.length; i++) {
        if (Array.isArray(classPriority[i]) && classPriority[i].indexOf(key) !== -1) return i;
      }
      return 999;
    }
    const idx = classPriority.indexOf(key);
    return idx === -1 ? 999 : idx;
  };
  // Keep the old name for backward compat — `classRankIdx` is used in several places.
  const classRankIdx = classGroupIdx;

  // Sort the matches based on the pacing mode.
  //
  // 'interleaved': round level first, then class → division → group.
  //                Result: R1 of all classes, then R2 of all, etc.
  //
  // 'serial': class first, then round → division → group.
  //           Result: All of class A first (R1, R2, …), then class B, etc.
  //
  // 'prioritized': class ranking (classPriority) first, then round → division → group.
  //                Result: The class at the top of the list, then the next, etc.
  const sortedMatches = [...tournament.matches].sort((a, b) => {
    // Phase priority: group < knockout (always)
    if (a.phase !== b.phase) return a.phase === 'group' ? -1 : 1;

    if (a.phase === 'group') {
      if (pacingMode === 'serial') {
        // Class first (alphabetically), then round
        if (a._classKey !== b._classKey)
          return (a._classKey || '').localeCompare(b._classKey || '');
        if ((a.roundIndex || 0) !== (b.roundIndex || 0))
          return (a.roundIndex || 0) - (b.roundIndex || 0);
      } else if (pacingMode === 'prioritized') {
        // Class by ranking, then round
        const ar = classRankIdx(a._classKey);
        const br = classRankIdx(b._classKey);
        if (ar !== br) return ar - br;
        if ((a.roundIndex || 0) !== (b.roundIndex || 0))
          return (a.roundIndex || 0) - (b.roundIndex || 0);
      } else {
        // 'interleaved' (default): round first, then class
        if ((a.roundIndex || 0) !== (b.roundIndex || 0))
          return (a.roundIndex || 0) - (b.roundIndex || 0);
        if (a._classKey !== b._classKey)
          return (a._classKey || '').localeCompare(b._classKey || '');
      }
      if (a.divisionId !== b.divisionId)
        return (a.divisionId || '').localeCompare(b.divisionId || '');
      return (a.groupName || '').localeCompare(b.groupName || '');
    }

    // Knockout order: early rounds are interleaved (A and B at the same level),
    // then the final block at the end. The B-finals are played BEFORE the A-finals, and
    // the A-final is dead last (the climax): … B-bronze → B-final → A-bronze → A-final.
    const roundOrder = {
      r32: 1,
      r16: 2,
      qf: 3,
      sf: 4,
      b_r32: 1,
      b_r16: 2,
      b_qf: 3,
      b_sf: 4,
      b_bronze: 5,
      b_f: 6,
      bronze: 7,
      f: 8,
    };
    const ar = roundOrder[a.round] || 99;
    const br = roundOrder[b.round] || 99;
    if (ar !== br) return ar - br;
    if (pacingMode === 'prioritized') {
      const ca = classRankIdx(a._classKey);
      const cb = classRankIdx(b._classKey);
      if (ca !== cb) return ca - cb;
    }
    if (a.classId !== b.classId) return (a.classId || '').localeCompare(b.classId || '');
    return (a.slot || 0) - (b.slot || 0);
  });

  // Build per-court availability for each day
  // courtsAvailable[dayIdx] = [{ court, currentMin, endMin, breaks }, ...]
  //
  // Per-day overrides are supported: each day can have its own `courts`, `matchDuration`
  // and `breaks` (time- or phase-based). If not set, the global defaults are used.
  const courtsAvailable = days.map((day, dayIdx) => {
    const [sh, sm] = (day.startTime || '09:00').split(':').map(Number);
    const [eh, em] = (day.endTime || '22:00').split(':').map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    const dayCourts = day.courts || courts;

    // Breaks for this day — only 'time' mode (etter_fase is ignored for KM
    // since KM has no phase concept)
    const dayBreaks = (day.breaks || [])
      .filter((b) => (b.mode || 'time') === 'time' && b.start)
      .map((b) => {
        const [h, m] = b.start.split(':').map(Number);
        return { startMin: h * 60 + m, endMin: h * 60 + m + b.durationMin };
      })
      .sort((a, b) => a.startMin - b.startMin);

    // Reserved days (A-class finals only) do not contribute courts to the
    // ordinary match schedule — they are kept empty here.
    return Array.from({ length: day.reserved ? 0 : dayCourts }, (_, ci) => ({
      court: ci + 1,
      currentMin: startMin,
      endMin,
      dayId: day.id || `day_${dayIdx + 1}`,
      date: day.date,
      dayIdx,
      breaks: dayBreaks,
    }));
  });

  // Helper: given a candidate start time and duration, skip past breaks that
  // would overlap the match. The match cannot run INSIDE a break.
  const skipKMBreaks = (startMin, durMin, breaks) => {
    let current = startMin;
    let changed = true;
    while (changed) {
      changed = false;
      for (const br of breaks) {
        if (current < br.endMin && current + durMin > br.startMin) {
          current = br.endMin;
          changed = true;
        }
      }
    }
    return current;
  };

  // Per-pair last-match-end time (across days — minutes since midnight + dayIdx * 1440)
  const pairLastEndAbsMin = new Map();

  const absMin = (slot, min) => slot.dayIdx * 1440 + min;

  // Phase separation: knockout cannot start before ALL group play is finished.
  // FOR THE DIVISION. Previously we used a global value (the last group match
  // across all classes), but that created unnecessary gaps in the calendar:
  // if MEN C had a late group match that ended at 17:00, WOMEN A's
  // semifinal (which logically could start at 16:00) was also pushed to 17:00.
  // Now we keep a MAP per divisionId — each division has its own phase separation.
  const groupStageEndByDiv = new Map();

  // ============================================================
  // SHOWCASE PLANNING: fixed time windows for A-class semis/finals.
  // Placed BEFORE the ordinary match schedule, on reserved courts in the
  // configured windows. Default OFF — skipped entirely when not activated,
  // so existing tournaments are unaffected.
  // ============================================================
  const showcasePlacedIds = new Set();
  const showcaseBlocks = []; // { date, court, fromMin, toMin } — blocks courts in the ordinary fill
  {
    const showcaseConfig = config.showcaseConfig || {};
    const hhmm = (s) => {
      if (!s || typeof s !== 'string' || !s.includes(':')) return null;
      const [h, m] = s.split(':').map(Number);
      return isNaN(h) || isNaN(m) ? null : h * 60 + m;
    };
    // Shared timeline per court (`date_court`) — prevents two classes from
    // double-booking the same court in overlapping windows.
    const courtTimeline = {};
    Object.keys(showcaseConfig).forEach((classKey) => {
      const cfg = showcaseConfig[classKey];
      if (!cfg || !cfg.enabled) return;
      // Two windows: semifinals, and final + bronze final — each window has
      // its own day, time range and reserved courts.
      [
        { w: cfg.semi, rounds: ['sf'] },
        { w: cfg.final, rounds: ['f', 'bronze'] },
      ].forEach(({ w, rounds }) => {
        if (!w || !w.dayId) return;
        const reservedCourts = Array.isArray(w.courts) ? w.courts.filter((c) => c > 0) : [];
        if (reservedCourts.length === 0) return;
        const fromMin = hhmm(w.from),
          toMin = hhmm(w.to);
        if (fromMin == null || toMin == null || fromMin >= toMin) return;
        const day = days.find((d) => d.id === w.dayId);
        if (!day) return;
        const winMatches = sortedMatches.filter(
          (m) =>
            m._classKey === classKey && rounds.includes(m.round) && !showcasePlacedIds.has(m.id),
        );
        // Place sequentially: pick the reserved court with the earliest free time
        // (shared timeline) that still fits the match within the window.
        winMatches.forEach((m) => {
          const dur = m.minutesPerMatch || 60;
          let pick = null,
            pickStart = null;
          reservedCourts.forEach((c) => {
            const tlKey = `${day.date}_${c}`;
            const start = Math.max(fromMin, courtTimeline[tlKey] ?? fromMin);
            if (start + dur <= toMin) {
              if (pick == null || start < pickStart) {
                pick = c;
                pickStart = start;
              }
            }
          });
          if (pick == null) return; // does not fit in the window — falls to the ordinary fill
          // Showcase matches are ALWAYS stretched to the full allocated window.
          // Semifinals run in parallel each on its own court (the UI enforces max 2
          // courts for semi, max 1 for final), so different matches in the same window
          // sit on different courts. Then both should fill the window — admin has
          // decided that the entire window time is reserved for that stage.
          const endMin = toMin;
          m.dayId = day.id;
          m.date = day.date;
          m.court = pick;
          m.startTime = formatMinutesAsTime(pickStart);
          m.endTime = formatMinutesAsTime(endMin);
          showcasePlacedIds.add(m.id);
          // BLOCKING for ordinary matches: the entire window (fromMin → toMin)
          // is reserved on this court.
          showcaseBlocks.push({ date: day.date, court: pick, fromMin, toMin });
          courtTimeline[`${day.date}_${pick}`] = endMin + bufferMin;
        });
      });
    });
  }

  for (const match of sortedMatches) {
    // Showcase matches are already placed in their fixed windows — skip
    if (showcasePlacedIds.has(match.id)) continue;
    const minutesNeeded = match.minutesPerMatch || 60;

    // Fetch the involved pairs to check pacing
    const involvedPairIds = [match.team1?.pairId, match.team2?.pairId].filter(Boolean);

    // For knockout matches pairId is often null (placeholder — the winner of the
    // previous round). The pacing check below finds no "previous match" for a placeholder,
    // and a final could then be placed right after the semi without rest time. We solve it
    // by fetching the end time of the matches that feed into this one (same division, previous
    // round in the bracket). Used as an additional minimum start time.
    let knockoutPrevEndAbs = null;
    if (
      match.phase === 'knockout' &&
      match.divisionId &&
      (match.team1?.from || match.team2?.from)
    ) {
      // e.g. team1.from='a_winner', prevRound='sf', prevSlot=1
      const sources = [match.team1, match.team2].filter((t) => t?.prevRound);
      sources.forEach((src) => {
        const feeder = sortedMatches.find(
          (mm) =>
            mm.divisionId === match.divisionId &&
            mm.bracket === match.bracket &&
            mm.round === src.prevRound &&
            mm.slot === src.prevSlot,
        );
        if (feeder && feeder.date && feeder.endTime) {
          // Convert to absolute minutes (dayIdx*1440 + end time)
          const dayIdx = courtsAvailable.findIndex((arr) => arr[0]?.date === feeder.date);
          if (dayIdx >= 0) {
            const [eh, em] = feeder.endTime.split(':').map(Number);
            const endAbs = dayIdx * 1440 + (eh || 0) * 60 + (em || 0);
            if (knockoutPrevEndAbs == null || endAbs > knockoutPrevEndAbs) {
              knockoutPrevEndAbs = endAbs;
            }
          }
        }
      });
    }

    // Soft gap preference: a slot counts as "right after one another" for a pair
    // if there is less than one match's breathing room since the pair's previous match.
    // Such slots get a time penalty in the slot choice — but not infinite, so
    // back-to-back is allowed rather than pushing the match unreasonably far.
    const gapThreshold = minRestMin + minutesNeeded;
    const gapPenalty = minutesNeeded;

    // Find the best slot: earliest possible across all days + courts, but respect pacing
    let best = null;
    for (let di = 0; di < courtsAvailable.length; di++) {
      for (let ci = 0; ci < courtsAvailable[di].length; ci++) {
        const slot = courtsAvailable[di][ci];
        let candidateStart = slot.currentMin;

        // Check pacing: each pair must have at least minRestMin since its previous match
        for (const pid of involvedPairIds) {
          const lastEnd = pairLastEndAbsMin.get(pid);
          if (lastEnd != null) {
            const earliestAllowed = lastEnd + minRestMin - slot.dayIdx * 1440;
            if (earliestAllowed > candidateStart) candidateStart = earliestAllowed;
          }
        }

        // Knockout-specific: respect minRest from the previous round in the same bracket.
        // This handles that the semi winner goes straight to the final — even though we
        // don't know who it is yet, we know they played the semi.
        if (knockoutPrevEndAbs != null) {
          const earliestAllowed = knockoutPrevEndAbs + minRestMin - slot.dayIdx * 1440;
          if (earliestAllowed > candidateStart) candidateStart = earliestAllowed;
        }

        // Shift past breaks if the candidate would overlap one
        candidateStart = skipKMBreaks(candidateStart, minutesNeeded, slot.breaks);

        // Phase separation: knockout matches cannot start before all group play in
        // the same division is finished — but the groups in OTHER divisions play
        // no role. This is how we avoid a late group match in one class
        // blocking fast knockouts in another.
        if (match.phase !== 'group' && match.divisionId) {
          const divGroupEnd = groupStageEndByDiv.get(match.divisionId) || 0;
          if (divGroupEnd > 0) {
            const minRel = divGroupEnd - slot.dayIdx * 1440;
            if (minRel > candidateStart) {
              candidateStart = minRel;
              candidateStart = skipKMBreaks(candidateStart, minutesNeeded, slot.breaks);
            }
          }
        }

        // Skip past showcase-reserved time ranges on this court/day
        if (showcaseBlocks.length > 0) {
          let scBumped = true;
          while (scBumped) {
            scBumped = false;
            for (const b of showcaseBlocks) {
              if (
                b.date === slot.date &&
                b.court === slot.court &&
                candidateStart < b.toMin &&
                candidateStart + minutesNeeded > b.fromMin
              ) {
                candidateStart = b.toMin;
                scBumped = true;
              }
            }
          }
          candidateStart = skipKMBreaks(candidateStart, minutesNeeded, slot.breaks);
        }

        if (candidateStart + minutesNeeded <= slot.endMin) {
          // Soft gap penalty: if an involved pair would have to play right after
          // its previous match here, add a time penalty so the scheduler rather
          // chooses a slot with breathing room — but only if it isn't too
          // far away (the penalty corresponds to ~one match).
          let penalty = 0;
          if (avoidBackToBack && involvedPairIds.length > 0) {
            const startAbs = absMin(slot, candidateStart);
            for (const pid of involvedPairIds) {
              const lastEnd = pairLastEndAbsMin.get(pid);
              if (lastEnd != null && startAbs - lastEnd < gapThreshold) {
                penalty = gapPenalty;
                break;
              }
            }
          }
          const score = absMin(slot, candidateStart) + penalty;
          const candidate = { di, ci, slot, startMin: candidateStart, score };
          if (!best || score < best.score) {
            best = candidate;
          }
        }
      }
    }

    if (!best) {
      // No room — leave the match unscheduled
      match.court = null;
      match.startTime = null;
      match.endTime = null;
      match.dayId = null;
      match.date = null;
      continue;
    }

    const startMin = best.startMin;
    const endMin = startMin + minutesNeeded;

    match.dayId = best.slot.dayId;
    match.date = best.slot.date;
    match.court = best.slot.court;
    match.startTime = formatMinutesAsTime(startMin);
    match.endTime = formatMinutesAsTime(endMin);

    // Update the slot — next free time on this court
    courtsAvailable[best.di][best.ci].currentMin = endMin + bufferMin;

    // Phase separation: keep track of when the last group match ends PER DIVISION.
    // This way other divisions' knockout can start earlier without waiting for
    // all group matches.
    if (match.phase === 'group' && match.divisionId) {
      const prev = groupStageEndByDiv.get(match.divisionId) || 0;
      groupStageEndByDiv.set(match.divisionId, Math.max(prev, absMin(best.slot, endMin)));
    }

    // Update the pair's last-time for pacing
    for (const pid of involvedPairIds) {
      pairLastEndAbsMin.set(pid, absMin(best.slot, endMin));
    }
  }

  // ============================================================
  // POST-PROCESSING: Court prioritization within a match rotation
  //
  // Group the matches placed on the same (date, start time) — that is a
  // "match rotation". Within the rotation, sort the matches by the user's
  // courtPriority rules, and assign the "most attractive" court (courtRank[0])
  // to the highest-priority match, etc.
  // ============================================================
  applyCourtPriority(tournament, showcasePlacedIds);

  return tournament;
}

// ---------- 10b) Court prioritization (reusable) ----------
// Redistributes courts within each match rotation (same date+start time) according to
// the config.courtPriority rules and config.courtRank. Can be re-run from
// the calendar to "reshuffle courts" without changing times.
//
// force (config.forceCourtPriority): allow a worse court to stand empty in order
// to get the highest-priority match on the best AVAILABLE court in the time slot —
// not just among the courts the rotation happened to land on.
//
// excludeIds: matches that should not be moved (e.g. showcase on fixed courts).
export function applyCourtPriority(tournament, excludeIds = new Set()) {
  const config = tournament.config || {};
  const courtPriority = config.courtPriority;
  const courtRank = config.courtRank;
  const force = !!config.forceCourtPriority;
  const totalCourts =
    config.courts || Math.max(1, ...(tournament.matches || []).map((m) => m.court || 1));
  if (!Array.isArray(courtPriority) || !courtPriority.some((p) => p.enabled)) return tournament;

  const pairById = new Map();
  (tournament.pairs || []).forEach((p) => pairById.set(p.id, p));
  const playerById = new Map();
  (config.allPlayers || []).forEach((p) => playerById.set(p.id, p));

  const matchPriorityValues = (match) => {
    const cls = (config.classes || []).find((c) => c.id === match.classId);
    const div = cls ? (cls.divisions || []).find((d) => d.id === match.divisionId) : null;
    const divName = div?.name || '';
    // Top-division priority: prefers division.level when it exists (league play
    // always sets level=1,2,3,…), falls back to A/B/C letters in the name
    // for the KM style. Both give lower number = higher priority.
    let class_a_first;
    if (typeof div?.level === 'number') {
      class_a_first = div.level - 1; // level 1 → 0, level 2 → 1, etc.
    } else if (/^[A-Z]$/.test(divName)) {
      class_a_first = divName.charCodeAt(0) - 65;
    } else {
      class_a_first = 99;
    }
    const gender = cls?.gender || '';
    const gender_priority = gender === 'M' ? 0 : gender === 'F' ? 1 : 2;
    const p1 = match.team1?.pairId ? pairById.get(match.team1.pairId) : null;
    const p2 = match.team2?.pairId ? pairById.get(match.team2.pairId) : null;
    if (!p1 || !p2) {
      return {
        class_a_first,
        gender_priority,
        highest_seed: Infinity,
        highest_team_elo_sum: -Infinity,
        highest_player_elo: -Infinity,
      };
    }
    const bestSeed = Math.min(p1.seed || 999, p2.seed || 999);
    const teamEloSum = (p1.eloSum || 0) + (p2.eloSum || 0);
    const elos = [p1.p1Id, p1.p2Id, p2.p1Id, p2.p2Id].map((id) => playerById.get(id)?.elo || 0);
    const topPlayerElo = Math.max(...elos);
    return {
      class_a_first,
      gender_priority,
      highest_seed: bestSeed,
      highest_team_elo_sum: -teamEloSum,
      highest_player_elo: -topPlayerElo,
    };
  };
  const compareMatchesByPriority = (a, b) => {
    const va = matchPriorityValues(a),
      vb = matchPriorityValues(b);
    for (const rule of courtPriority) {
      if (!rule.enabled) continue;
      const da = va[rule.id],
        db = vb[rule.id];
      if (da !== db) return da - db;
    }
    return 0;
  };

  // Which courts are occupied in each time slot (all matches, incl. showcase) —
  // used in force mode to find free courts.
  const usedBySlot = new Map();
  (tournament.matches || []).forEach((m) => {
    if (!m.date || !m.startTime || !m.court) return;
    const key = `${m.date}_${m.startTime}`;
    if (!usedBySlot.has(key)) usedBySlot.set(key, new Set());
    usedBySlot.get(key).add(m.court);
  });

  // Build rotations (only matches that can be moved)
  const rotations = new Map();
  (tournament.matches || []).forEach((m) => {
    if (!m.date || !m.startTime || !m.court) return;
    if (excludeIds.has(m.id)) return;
    const key = `${m.date}_${m.startTime}`;
    if (!rotations.has(key)) rotations.set(key, []);
    rotations.get(key).push(m);
  });

  const rankedCourts = Array.isArray(courtRank) && courtRank.length > 0 ? courtRank : null;
  // How many of the top courts are actually "ranked". The rest are distributed
  // randomly. null/undefined = all are ranked (old behavior).
  const rankedCount =
    typeof config.rankedCourtCount === 'number' && config.rankedCourtCount >= 0
      ? config.rankedCourtCount
      : null;
  const byRank = (a, b) => {
    if (!rankedCourts) return a - b;
    const ai = rankedCourts.indexOf(a),
      bi = rankedCourts.indexOf(b);
    if (ai === -1 && bi === -1) return a - b;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  };
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  rotations.forEach((rotMatches, key) => {
    if (rotMatches.length === 0) return;
    const banesInUse = [...new Set(rotMatches.map((m) => m.court))];
    let candidateCourts;
    if (force) {
      const usedHere = usedBySlot.get(key) || new Set();
      const usedByOthers = new Set([...usedHere].filter((c) => !banesInUse.includes(c)));
      const allCourts = Array.from({ length: totalCourts }, (_, i) => i + 1);
      candidateCourts = allCourts.filter((c) => !usedByOthers.has(c));
    } else {
      candidateCourts = banesInUse;
    }
    const orderedBanes = [...candidateCourts].sort(byRank);
    // Split the courts into a ranked part (top N) and a random part (the rest).
    const n =
      rankedCount == null ? orderedBanes.length : Math.min(rankedCount, orderedBanes.length);
    const finalBanes = [...orderedBanes.slice(0, n), ...shuffle(orderedBanes.slice(n))];
    const orderedMatches = [...rotMatches].sort(compareMatchesByPriority);
    orderedMatches.forEach((m, idx) => {
      if (idx < finalBanes.length) m.court = finalBanes[idx];
    });
  });

  return tournament;
}
// Compute the total time/court requirement for the whole tournament (sum over classes/divisions)
export function calcKlubbmesterskapBudget(tournament) {
  let groupMatches = 0,
    aMatches = 0,
    bMatches = 0;
  tournament.config.classes.forEach((cls) => {
    cls.divisions.forEach((div) => {
      const c = calculateKMMatchCount({
        groupCount: div.groupCount,
        pairsPerGroup:
          div.pairCount && div.groupCount ? Math.ceil(div.pairCount / div.groupCount) : 0,
        advanceTopN: div.advanceTopN,
        withConsolation: div.consolationBracket,
        withBronze: kmBronzeFinalOn(div),
      });
      groupMatches += c.groupMatches;
      aMatches += c.aBracketMatches;
      bMatches += c.bBracketMatches;
    });
  });
  const total = groupMatches + aMatches + bMatches;
  // Average match time across phases
  const avgMin =
    total > 0
      ? (groupMatches * KM_DEFAULT_GROUP_MINUTES +
          (aMatches + bMatches) * KM_DEFAULT_KNOCKOUT_MINUTES) /
        total
      : 0;
  const budget = calculateKMTimeBudget({
    matchCount: total,
    courts: tournament.config.courts,
    minutesPerMatch: avgMin,
    bufferMinutes: tournament.config.bufferMinutes,
  });
  // Available court time in the calendar
  const availableMinutes = (tournament.config.days || []).reduce((sum, day) => {
    const [h1, m1] = (day.startTime || '00:00').split(':').map(Number);
    const [h2, m2] = (day.endTime || '00:00').split(':').map(Number);
    return sum + (h2 * 60 + m2 - (h1 * 60 + m1));
  }, 0);
  const availableCourtMinutes = availableMinutes * (tournament.config.courts || 1);
  const neededCourtMinutes = total * avgMin;
  return {
    groupMatches,
    aMatches,
    bMatches,
    total,
    slotsNeeded: budget.slots,
    totalMinutes: budget.totalMinutes,
    hoursDecimal: budget.hoursDecimal,
    availableMinutes,
    availableCourtMinutes,
    neededCourtMinutes,
    utilizationPct:
      availableCourtMinutes > 0
        ? Math.round((neededCourtMinutes / availableCourtMinutes) * 100)
        : 0,
    fits: neededCourtMinutes <= availableCourtMinutes,
  };
}
